import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { debugInfo, debugError } from "../utils/debug";
import SettingsModal from "./SettingsModal";
import FileUpload from "./FileUpload";
import { parseAllCrdFiles, getCrdDocument } from "../services/crdApi";

const Dashboard = ({ onLogout }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState({
    totalAccounts: 0,
    activeContracts: 0,
    pendingRequests: 0,
    completedTasks: 0,
  });
  const [loading, setLoading] = useState(false); // Не загружаем данные автоматически
  const [refreshing, setRefreshing] = useState(false); // Локальный индикатор обновления таблицы
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState(null);
  const uploadInputRef = useRef(null);
  const [dataSource, setDataSource] = useState(''); // 'cache', 'fresh', или пустая строка

  // Состояние фильтров - по умолчанию текущий год
  const getCurrentYearDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    return {
      from: `${year}-01-01`,
      to: `${year}-12-31`
    };
  };
  
  const yearDates = getCurrentYearDates();
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('from') || yearDates.from);
  const [dateTo, setDateTo] = useState(() => searchParams.get('to') || yearDates.to);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || "");
  const [contractorFilter, setContractorFilter] = useState("");
  const [numberFilter, setNumberFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [amountFilter, setAmountFilter] = useState(null);
  const [paymentDirectionFilter, setPaymentDirectionFilter] = useState('all'); // 'all' | 'outgoing' | 'incoming'

  // Синхронизируем фильтры с URL
  useEffect(() => {
    const params = {};
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (searchQuery) params.q = searchQuery;
    setSearchParams(params, { replace: true });
  }, [dateFrom, dateTo, searchQuery]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  // Данные таблицы - изначально пустая таблица
  const [tableRows, setTableRows] = useState([]);
  
  // Данные Регистра НДС
  const [vatRegister, setVatRegister] = useState([]);

  // E2E test hook: exposes setVatRegister on window for puppeteer tests
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__test_setVatRegister = setVatRegister;
    }
    return () => { delete window.__test_setVatRegister; };
  }, [setVatRegister]);

  // Расцветка строк: сопоставление документов с Регистром НДС на стороне фронта.
  // Ключ — «НОМЕР|ДАТА» (UPPER TRIM), что исключает ложные совпадения одинаковых номеров из разных периодов.
  const vatRegisterLookup = useMemo(() => {
    const salesBook = new Set();
    const purchaseBook = new Set();
    // Останавливаемся на пробеле или запятой: «Реализация № 16_2026Р от 30.03.2026» → «16_2026Р»
    const numRe = /№\s*([^\s,]+)/;
    // Комплексный номер — содержит не-цифровые символы (буквы/подчёркивание).
    // Для таких номеров используем поиск только по НОМЕРУ (без даты), т.к. дата в реестре НДС может
    // отличаться от даты документа (напр. авансовый СФ выставлен в феврале, зачтён в книге покупок в марте).
    // Чисто числовые номера (типа «216») — строго НОМЕР|ДАТА чтобы избежать ложных совпадений.
    const isComplexNum = (n) => /\D/.test(n); // есть хоть один не-цифровой символ
    for (const entry of vatRegister) {
      // Предпочитаем готовое поле number (заполненное Go), иначе разбираем document
      const rawNum = entry.number || (numRe.exec(entry.document || '') || [])[1] || '';
      const number = rawNum.trim().toUpperCase();
      if (!number) continue;
      // Нормализуем дату: DD.MM.YYYY → YYYY-MM-DD (если была загружена как текст)
      const rawDate = (entry.date || '').trim();
      const ruDate = rawDate.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      const date = ruDate ? `${ruDate[3]}-${ruDate[2]}-${ruDate[1]}` : rawDate;
      // Комплексный номер (07_26ПР, 03_2026Д) — ищем без даты (дата в реестре и дата документа могут расходиться)
      // Простой числовой номер (216) — ищем строго по НОМЕР|ДАТА чтобы не было ложных совпадений
      const key = isComplexNum(number) ? number : (date ? `${number}|${date}` : number);
      const section = (entry.section || '').toLowerCase().trim();
      const isSales = section.includes('продаж') || (section.includes('9') && !section.includes('покупок'));
      const isPurchase = section.includes('покупок') || (section.includes('8') && !section.includes('9'));
      if (isSales) {
        salesBook.add(key);
      } else if (isPurchase) {
        purchaseBook.add(key);
      }
    }
    if (vatRegister.length > 0) {
      console.log('🟢 vatRegisterLookup built:', vatRegister.length, 'entries,',
        salesBook.size, 'salesBook,', purchaseBook.size, 'purchaseBook');
    }
    return { salesBook, purchaseBook };
  }, [vatRegister]);

  // Флаг для предотвращения множественных загрузок
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Состояние для модального окна с данными CRD
  const [isCrdModalOpen, setIsCrdModalOpen] = useState(false);
  const [selectedCrdData, setSelectedCrdData] = useState(null);
  const [crdModalTab, setCrdModalTab] = useState('normal');

  // Состояние прямого создания в СБИС: id → ссылка на созданный документ
  const [createdSbisLinks, setCreatedSbisLinks] = useState({});
  // Строки, для которых документ был успешно проведён в СБИС
  const [conductedRows, setConductedRows] = useState(new Set());
  // Строки, для которых сейчас идёт проведение документа
  const [conductingRows, setConductingRows] = useState(new Set());
  // Локальный статус проведения для linked-аванса, если отдельная строка аванса не найдена
  const [linkedAdvanceConducted, setLinkedAdvanceConducted] = useState({});
  // Строки, для которых сейчас идёт создание (spinning)
  const [creatingRows, setCreatingRows] = useState(new Set());
  // Строки, для которых сейчас идёт удаление из СБИС
  const [deletingRows, setDeletingRows] = useState(new Set());

    // Состояние выделения строк
    const [selectedRows, setSelectedRows] = useState([]);

    // Обработчик выделения строки
    const handleSelectRow = (id) => {
      setSelectedRows((prev) =>
        prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
      );
    };

    // Обработчик выделения всех
    const handleSelectAll = (e) => {
      if (e.target.checked) {
        setSelectedRows(filteredTableRows.map((row) => row.id));
      } else {
        setSelectedRows([]);
      }
    };

    const parseDateToTs = (value) => {
      const str = String(value || '').trim();
      if (!str) return null;

      if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        const ts = Date.parse(str);
        return Number.isNaN(ts) ? null : ts;
      }

      const ru = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (ru) {
        const [, dd, mm, yyyy] = ru;
        const ts = Date.parse(`${yyyy}-${mm}-${dd}T00:00:00`);
        return Number.isNaN(ts) ? null : ts;
      }

      const ts = Date.parse(str);
      return Number.isNaN(ts) ? null : ts;
    };

    const compareDateSymbol = (leftDate, rightDate) => {
      const leftTs = parseDateToTs(leftDate);
      const rightTs = parseDateToTs(rightDate);
      if (leftTs == null || rightTs == null) return '';
      if (leftTs < rightTs) return '<';
      if (leftTs > rightTs) return '>';
      return '=';
    };

    const normalizeDocNumberForSort = (value) =>
      String(value || '').toUpperCase().replace(/[\s.;,]/g, '').replace(/[-–—]/g, '_');

    const isAdvanceDocForSort = (row) =>
      /\/АВ/i.test(row.sbisNumber || '') || /\/АВ/i.test(row.number || '') || /ПР$/i.test(row.number || '') || /ПР$/i.test(row.sbisNumber || '');

    const advanceDateByLink = new Map();
    const advanceDateByNumber = new Map();
    const setMinTs = (map, key, ts) => {
      if (!key || ts == null) return;
      const prev = map.get(key);
      if (prev == null || ts < prev) map.set(key, ts);
    };

    tableRows.forEach((row) => {
      if (!isAdvanceDocForSort(row)) return;
      const ts = parseDateToTs(row.date);
      if (ts == null) return;

      setMinTs(advanceDateByLink, String(row.sbisLink || ''), ts);
      setMinTs(advanceDateByNumber, normalizeDocNumberForSort(row.number), ts);
      setMinTs(advanceDateByNumber, normalizeDocNumberForSort(row.sbisNumber), ts);
    });

    // Предвычисление ключей сортировки по суб-столбцу СБИС (один раз на строку, ДО .sort())
    // Это гарантирует консистентность компаратора и устраняет «заполненные→пустые→заполненные».
    const isSortBySbisDate = sortConfig.key === 'sbisPaymentDate' || sortConfig.key === 'sbisAdvanceDate' || sortConfig.key === 'sbisShipmentDate';
    const sbisDateSortKey = new Map();
    if (isSortBySbisDate) {
      const _isPaymentDocRow = (row) => row.isPayment || row.docType === 'ВходящийПлатеж' || row.docType === 'ИсходящийПлатеж' || row.docType === 'БанковскаяВыписка';
      const _isAdvanceDocRow = (row) => /\/АВ/i.test(row.sbisNumber || '') || /\/АВ/i.test(row.number || '') || /ПР$/i.test(row.number || '') || /ПР$/i.test(row.sbisNumber || '');
      // Порядок проверок совпадает с рендером: advance → splitPayment → isPaymentDoc → shipment
      const _getSbisCat = (row) => {
        if (_isAdvanceDocRow(row)) return 'advance';
        const isSplit = !!row.splitByPayment;
        if (isSplit) {
          const payTs = parseDateToTs(String(row.payments?.[0]?.date || ''));
          const docTs = parseDateToTs(String(row.date || ''));
          const showFlow = payTs != null && docTs != null && payTs < docTs;
          if (!showFlow) return 'payment';
        }
        if (_isPaymentDocRow(row)) return 'payment';
        return 'shipment';
      };
      // Минимальная (ранняя) дата оплаты — по timestamp, не по строке
      const _getMinPaymentTs = (row) => {
        if (!Array.isArray(row.payments) || row.payments.length === 0) return null;
        const tss = row.payments.map(p => parseDateToTs(String(p?.date || ''))).filter(t => t != null);
        return tss.length > 0 ? Math.min(...tss) : null;
      };
      const _getLinkedAdvanceTs = (row) => {
        const byLink = advanceDateByLink.get(String(row.linkedInvoiceLink || ''));
        if (byLink != null) return byLink;
        const key = normalizeDocNumberForSort(row.linkedInvoiceNumber || '');
        return advanceDateByNumber.get(key) ?? null;
      };
      const _computeKey = (row) => {
        const cat = _getSbisCat(row);
        if (sortConfig.key === 'sbisPaymentDate') {
          const payTs = _getMinPaymentTs(row);
          if (payTs != null) return payTs;
          if (cat === 'payment') return parseDateToTs(row.date);
          return null;
        }
        if (sortConfig.key === 'sbisAdvanceDate') {
          if (cat === 'advance') return parseDateToTs(row.date);
          const linked = _getLinkedAdvanceTs(row);
          if (linked != null) return linked;
          return parseDateToTs(row.linkedInvoiceDate) ?? null;
        }
        // sbisShipmentDate
        if (cat === 'shipment') return parseDateToTs(row.date);
        if (cat === 'advance' && row.closingDoc?.date) return parseDateToTs(row.closingDoc.date);
        return null;
      };
      for (const row of tableRows) {
        sbisDateSortKey.set(row.id, _computeKey(row));
      }
    }

    // Фильтрация и сортировка данных таблицы
    const filteredAndSortedTableRows = tableRows
      .filter((row) => {
        // Фильтры по клику на ячейки
        if (contractorFilter && String(row.contractor || '') !== contractorFilter) return false;
        if (numberFilter && String(row.number || '') !== numberFilter) return false;
        if (dateFilter && String(row.date || '') !== dateFilter) return false;
        if (amountFilter !== null && row.amount !== amountFilter) return false;
        if (paymentDirectionFilter !== 'all') {
          const rowIsPaymentDoc = row.isPayment || row.docType === 'ВходящийПлатеж' || row.docType === 'ИсходящийПлатеж' || row.docType === 'БанковскаяВыписка';
          let rowCircleIsIncoming;
          if (rowIsPaymentDoc) {
            rowCircleIsIncoming = row.docType === 'ВходящийПлатеж';
          } else {
            rowCircleIsIncoming = row.type !== 'Входящая';
          }
          if (paymentDirectionFilter === 'incoming' && !rowCircleIsIncoming) return false;
          if (paymentDirectionFilter === 'outgoing' && rowCircleIsIncoming) return false;
        }
        
        if (!searchQuery.trim()) return true;
        
        const query = searchQuery.toLowerCase();
        // Преобразуем в строку перед вызовом toLowerCase (поля могут быть числами)
        const number = String(row.number || '').toLowerCase();
        const sbisNum = String(row.sbisNumber || '').toLowerCase();
        const contractor = String(row.contractor || '').toLowerCase();
        const amount = String(row.amount || '').toLowerCase();
        const vat20 = String(row.vat20 || '').toLowerCase();
        const vat22 = String(row.vat22 || '').toLowerCase();
        const closingNum = String(row.closingDoc?.number || '').toLowerCase();
        const linkedNum = String(row.linkedInvoiceNumber || '').toLowerCase();
        
        return number.includes(query) || sbisNum.includes(query) || contractor.includes(query)
          || amount.includes(query) || vat20.includes(query) || vat22.includes(query)
          || closingNum.includes(query) || linkedNum.includes(query);
      })
      .sort((a, b) => {
        if (!sortConfig.key) return 0;
        
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Особая обработка для даты
        if (sortConfig.key === 'date') {
          aValue = parseDateToTs(aValue);
          bValue = parseDateToTs(bValue);
        }

        // Сортировка по суб-столбцу СБИС — используем предвычисленные ключи
        if (isSortBySbisDate) {
          const aTs = sbisDateSortKey.get(a.id) ?? null;
          const bTs = sbisDateSortKey.get(b.id) ?? null;
          // nulls to the end regardless of direction
          if (aTs == null && bTs == null) return 0;
          if (aTs == null) return 1;
          if (bTs == null) return -1;
          aValue = aTs;
          bValue = bTs;
        }
        
        // Особая обработка для суммы
        if (sortConfig.key === 'amount' || sortConfig.key === 'vat20' || sortConfig.key === 'vat22') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // Сравнение
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        // Детерминированный tie-break для стабильной перестановки всех строк
        const aFallback = `${String(a.date || '')}|${String(a.number || '')}|${String(a.id || '')}`;
        const bFallback = `${String(b.date || '')}|${String(b.number || '')}|${String(b.id || '')}`;
        return aFallback.localeCompare(bFallback);
      });

    // Для обратной совместимости оставляем старое название
    const filteredTableRows = filteredAndSortedTableRows;

    // Обработчик сортировки
    const handleSort = (key) => {
      setSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
    };

  // Обработчик фильтрации - вызывает синхронизацию SBIS
  const handleFilter = async () => {
    debugInfo("Dashboard", "Filtering data", { dateFrom, dateTo });
    // Загружаем данные с выбранными датами
    setRefreshing(true);
    setDataSource('fresh');
    await loadInvoiceData();
    setRefreshing(false);
  };

  // Обработчик создания документа в СБИС
  const handleCreateSbisDocument = async (rowData) => {
    debugInfo("Dashboard", "Creating SBIS document", { rowData });
    
    try {
      // Проверяем наличие необходимых данных
      if (!rowData || !rowData.ukey) {
        alert('Ошибка: отсутствует UKEY документа');
        return;
      }
      
      if (!rowData.tableName) {
        alert('Ошибка: отсутствует информация о таблице CRD');
        return;
      }
      
      if (!rowData.yearMonth) {
        alert('Ошибка: отсутствует информация о периоде документа');
        return;
      }
      
      // Показываем индикатор загрузки
      setSelectedCrdData({
        ...rowData,
        creating: true
      });
      
      // Импортируем API
      const { createSbisDocument } = await import("../services/sbisApi");
      
      // Создаем документ
      const result = await createSbisDocument(
        user.email,
        rowData.tableName,
        rowData.ukey,
        rowData.yearMonth,
        'invoice' // По умолчанию УПД
      );
      
      if (result.success) {
        debugInfo("Dashboard", "SBIS document created successfully", result);
        
        // Обновляем данные модального окна — кнопка сменится на "Открыть в СБИС"
        setSelectedCrdData({
          ...rowData,
          creating: false,
          sbisLink: result.document.link,
          sbisCreated: true,
          hasSbis: true
        });
        
        // Обновляем таблицу
        handleRefresh();
      } else {
        throw new Error(result.error || 'Неизвестная ошибка');
      }
      
    } catch (error) {
      debugError("Dashboard", "Error creating SBIS document", error);
      
      setSelectedCrdData({
        ...rowData,
        creating: false,
        createError: error.message
      });
    }
  };

  // Удаление документа из СБИС
  const handleDeleteSbisDocument = async (row) => {
    const link = row.sbisLink || createdSbisLinks[row.id] || '';
    if (!link) {
      alert('Ошибка: ссылка на документ СБИС не найдена');
      return;
    }
    // Extract GUID from sbis link: ?guid=XXXX&...
    let guid = '';
    try {
      const url = new URL(link);
      guid = url.searchParams.get('guid') || '';
    } catch (_) {}
    if (!guid) {
      alert('Ошибка: не удалось извлечь идентификатор документа из ссылки СБИС');
      return;
    }
    if (!window.confirm(`Удалить документ №${row.number} от ${row.date} из СБИС?`)) return;

    setDeletingRows(prev => new Set(prev).add(row.id));
    try {
      const { deleteSbisDocument } = await import('../services/sbisApi');
      const result = await deleteSbisDocument(user.email, guid);
      if (result.success) {
        // Clear sbisLink from local state
        setCreatedSbisLinks(prev => { const next = { ...prev }; delete next[row.id]; return next; });
        setConductedRows(prev => { const next = new Set(prev); next.delete(row.id); return next; });
        setTableRows(prev => {
          const updated = prev.map(r => {
            if (r.id !== row.id) return r;
            const cleaned = { ...r, sbisLink: '', hasSbis: false, processed: false };
            // Reset source so hasSbis computed flag also becomes false
            if (cleaned.source === 'sbis') {
              cleaned.source = cleaned.hasParsed || (cleaned.sources && (cleaned.sources.includes('parsed') || cleaned.sources.includes('crd'))) ? 'parsed' : '';
            }
            if (cleaned.sources && Array.isArray(cleaned.sources)) {
              cleaned.sources = cleaned.sources.filter(s => s !== 'sbis');
            }
            return cleaned;
          });
          return updated;
        });
      } else {
        alert('Ошибка удаления: ' + (result.error || 'неизвестная ошибка'));
      }
    } catch (error) {
      alert('Ошибка удаления документа из СБИС: ' + error.message);
    } finally {
      setDeletingRows(prev => { const next = new Set(prev); next.delete(row.id); return next; });
    }
  };

  // Проведение документа в СБИС
  const handleConductSbisDocument = async (row) => {
    // Use row.originalId (the actual SBIS Идентификатор) — NOT the URL guid which is different
    const documentGuid = row.originalId || '';
    if (!documentGuid) {
      alert('Ошибка: не удалось определить идентификатор документа СБИС');
      return;
    }

    // Determine SBIS document type from row data
    const docTypeMap = {
      invoice_out: 'ФактураИсх',
      invoice_in: 'ФактураВх',
      shipping_out: 'ДокОтгрИсх',
      shipping_in: 'ДокОтгрВх',
    };
    const docType = row.docType || docTypeMap[row.type] || '';

    setConductingRows(prev => new Set(prev).add(row.id));
    try {
      const { conductSbisDocument } = await import('../services/sbisApi');
      const result = await conductSbisDocument(user.email, documentGuid, docType);
      if (result.success) {
        // Обновляем флаг processed локально
        setTableRows(prev => {
          const updated = prev.map(r =>
            r.id === row.id ? { ...r, processed: true } : r
          );
          return updated;
        });
      } else {
        throw new Error(result.error || 'Неизвестная ошибка');
      }
    } catch (error) {
      debugError('Dashboard', 'Error conducting SBIS document', error);
      alert(`❌ Ошибка проведения документа в СБИС:\n\n${error.message}`);
    } finally {
      setConductingRows(prev => { const next = new Set(prev); next.delete(row.id); return next; });
    }
  };

  // Переключение проведения документа в СБИС (провести/снять проведение)
  const handleToggleSbisDocumentConduct = async (row, shouldConduct) => {
    // Use row.originalId (the actual SBIS Идентификатор) — NOT the URL guid which is different
    const documentGuid = row.originalId || '';
    if (!documentGuid) {
      alert('Ошибка: не удалось определить идентификатор документа СБИС');
      return;
    }

    const docTypeMap = {
      invoice_out: 'ФактураИсх',
      invoice_in: 'ФактураВх',
      shipping_out: 'ДокОтгрИсх',
      shipping_in: 'ДокОтгрВх',
    };
    const docType = row.docType || docTypeMap[row.type] || '';

    setConductingRows(prev => new Set(prev).add(row.id));
    try {
      const { conductSbisDocument, unconductSbisDocument } = await import('../services/sbisApi');
      const result = shouldConduct
        ? await conductSbisDocument(user.email, documentGuid, docType)
        : await unconductSbisDocument(user.email, documentGuid, docType);

      if (result.success) {
        setTableRows(prev => prev.map(r =>
          r.id === row.id ? { ...r, processed: shouldConduct } : r
        ));
      } else {
        throw new Error(result.error || 'Неизвестная ошибка');
      }
    } catch (error) {
      debugError('Dashboard', 'Error toggling SBIS conduct state', error);
      alert(`❌ Ошибка изменения проведения документа в СБИС:\n\n${error.message}`);
    } finally {
      setConductingRows(prev => { const next = new Set(prev); next.delete(row.id); return next; });
    }
  };

  const normalizeDocNumber = (v) => String(v || '').toUpperCase().replace(/[\s.;,]/g, '');

  const extractGuidFromSbisLink = (link) => {
    const s = String(link || '');
    if (!s) return '';
    const byQuery = s.match(/[?&]guid=([0-9a-fA-F-]{36})/);
    if (byQuery && byQuery[1]) return byQuery[1];
    const byPath = s.match(/\/doc\/([0-9a-fA-F-]{36})/);
    if (byPath && byPath[1]) return byPath[1];
    return '';
  };

  const getLinkedAdvanceStateKey = (row, linkedInvNum) => {
    const guid = extractGuidFromSbisLink(row?.linkedInvoiceLink || '');
    const keyPart = guid || normalizeDocNumber(linkedInvNum) || String(row?.id || '');
    return `linked_adv_state_${keyPart}`;
  };

  const getClosingShipmentStateKey = (row) => {
    const link = row?.closingDoc?.sbisLink || '';
    const guid = extractGuidFromSbisLink(link);
    const number = normalizeDocNumber(row?.closingDoc?.number || '');
    const date = String(row?.closingDoc?.date || '');
    const keyPart = guid || `${number}_${date}` || String(row?.id || '');
    return `closing_ship_state_${keyPart}`;
  };

  // Toggle conduct for linked advance invoice in cases when table row is not directly resolvable
  const handleToggleLinkedAdvanceConduct = async ({ row, advRow, linkedInvNum, shouldConduct }) => {
    if (advRow) {
      await handleToggleSbisDocumentConduct(advRow, shouldConduct);
      return;
    }

    const documentGuid = extractGuidFromSbisLink(row?.linkedInvoiceLink);
    if (!documentGuid) {
      alert('Ошибка: не удалось определить идентификатор связанного авансового СФ');
      return;
    }

    const fallbackId = `linked_${row?.id || linkedInvNum || documentGuid}`;
    const linkedStateKey = getLinkedAdvanceStateKey(row, linkedInvNum);
    const docType = row?.type === 'Входящая' ? 'ФактураВх' : 'ФактураИсх';

    setConductingRows(prev => new Set(prev).add(fallbackId));
    try {
      const { conductSbisDocument, unconductSbisDocument } = await import('../services/sbisApi');
      const result = shouldConduct
        ? await conductSbisDocument(user.email, documentGuid, docType)
        : await unconductSbisDocument(user.email, documentGuid, docType);

      if (!result.success) {
        throw new Error(result.error || 'Неизвестная ошибка');
      }

      setLinkedAdvanceConducted(prev => ({ ...prev, [linkedStateKey]: shouldConduct }));

      const linkedKey = normalizeDocNumber(linkedInvNum);
      setTableRows(prev => prev.map(r => {
        const sameByLink = row?.linkedInvoiceLink && r.sbisLink === row.linkedInvoiceLink;
        const sameByNumber = linkedKey && (normalizeDocNumber(r.number) === linkedKey || normalizeDocNumber(r.sbisNumber) === linkedKey);
        return (sameByLink || sameByNumber) ? { ...r, processed: shouldConduct } : r;
      }));
    } catch (error) {
      debugError('Dashboard', 'Error toggling linked advance conduct state', error);
      alert(`❌ Ошибка изменения проведения авансового СФ в СБИС:\n\n${error.message}`);
    } finally {
      setConductingRows(prev => { const next = new Set(prev); next.delete(fallbackId); return next; });
    }
  };

  const handleDeleteLinkedAdvanceDocument = async ({ row, advRow, linkedInvNum }) => {
    if (advRow) {
      await handleDeleteSbisDocument(advRow);
      return;
    }

    const link = row?.linkedInvoiceLink || '';
    const guid = extractGuidFromSbisLink(link);
    if (!guid) {
      alert('Ошибка: не удалось определить идентификатор связанного авансового СФ');
      return;
    }

    if (!window.confirm(`Удалить авансовый СФ №${linkedInvNum || ''} из СБИС?`)) return;

    const fallbackDeleteId = `linked_del_${row?.id || linkedInvNum || guid}`;
  const linkedStateKey = getLinkedAdvanceStateKey(row, linkedInvNum);
    setDeletingRows(prev => new Set(prev).add(fallbackDeleteId));
    try {
      const { deleteSbisDocument } = await import('../services/sbisApi');
      const result = await deleteSbisDocument(user.email, guid);
      if (!result.success) {
        throw new Error(result.error || 'Неизвестная ошибка');
      }

      setLinkedAdvanceConducted(prev => ({ ...prev, [linkedStateKey]: false }));

      const linkedKey = normalizeDocNumber(linkedInvNum);
      setTableRows(prev => prev.map(r => {
        const sameByLink = row?.linkedInvoiceLink && (r.sbisLink === row.linkedInvoiceLink || r.linkedInvoiceLink === row.linkedInvoiceLink);
        const sameByNumber = linkedKey && (normalizeDocNumber(r.number) === linkedKey || normalizeDocNumber(r.sbisNumber) === linkedKey || normalizeDocNumber(r.linkedInvoiceNumber) === linkedKey);
        if (!sameByLink && !sameByNumber) return r;
        return {
          ...r,
          sbisLink: sameByLink ? '' : r.sbisLink,
          hasSbis: sameByLink ? false : r.hasSbis,
          processed: sameByLink ? false : r.processed,
          hasLinkedInvoice: false,
          linkedInvoiceLink: '',
          linkedInvoiceNumber: ''
        };
      }));
    } catch (error) {
      alert('Ошибка удаления авансового СФ из СБИС: ' + error.message);
    } finally {
      setDeletingRows(prev => { const next = new Set(prev); next.delete(fallbackDeleteId); return next; });
    }
  };

  const handleToggleClosingShipmentConduct = async ({ row, shouldConduct }) => {
    const documentGuid = extractGuidFromSbisLink(row?.closingDoc?.sbisLink || '');
    if (!documentGuid) {
      alert('Ошибка: не удалось определить идентификатор реализации');
      return;
    }

    const fallbackConductId = `closing_${row?.id || row?.closingDoc?.number || documentGuid}`;
    const closingStateKey = getClosingShipmentStateKey(row);
    const docType = row?.type === 'Входящая' ? 'ДокОтгрВх' : 'ДокОтгрИсх';

    setConductingRows(prev => new Set(prev).add(fallbackConductId));
    try {
      const { conductSbisDocument, unconductSbisDocument } = await import('../services/sbisApi');
      const result = shouldConduct
        ? await conductSbisDocument(user.email, documentGuid, docType)
        : await unconductSbisDocument(user.email, documentGuid, docType);

      if (!result.success) {
        throw new Error(result.error || 'Неизвестная ошибка');
      }

      setTableRows(prev => prev.map(r =>
        r.id === row.id
          ? { ...r, closingDoc: { ...(r.closingDoc || {}), processed: shouldConduct } }
          : r
      ));
      setLinkedAdvanceConducted(prev => ({ ...prev, [closingStateKey]: shouldConduct }));
    } catch (error) {
      debugError('Dashboard', 'Error toggling closing shipment conduct state', error);
      alert(`❌ Ошибка изменения проведения реализации в СБИС:\n\n${error.message}`);
    } finally {
      setConductingRows(prev => { const next = new Set(prev); next.delete(fallbackConductId); return next; });
    }
  };

  const handleDeleteClosingShipmentDocument = async ({ row }) => {
    const link = row?.closingDoc?.sbisLink || '';
    const guid = extractGuidFromSbisLink(link);
    const closingNum = row?.closingDoc?.number || '';
    if (!guid) {
      alert('Ошибка: не удалось определить идентификатор реализации');
      return;
    }

    if (!window.confirm(`Удалить реализацию №${closingNum || ''} из СБИС?`)) return;

    const fallbackDeleteId = `closing_del_${row?.id || closingNum || guid}`;
    const closingStateKey = getClosingShipmentStateKey(row);
    setDeletingRows(prev => new Set(prev).add(fallbackDeleteId));
    try {
      const { deleteSbisDocument } = await import('../services/sbisApi');
      const result = await deleteSbisDocument(user.email, guid);
      if (!result.success) {
        throw new Error(result.error || 'Неизвестная ошибка');
      }

      setLinkedAdvanceConducted(prev => ({ ...prev, [closingStateKey]: false }));
      setTableRows(prev => prev.map(r =>
        r.id === row.id
          ? { ...r, closingDoc: null }
          : r
      ));
    } catch (error) {
      alert('Ошибка удаления реализации из СБИС: ' + error.message);
    } finally {
      setDeletingRows(prev => { const next = new Set(prev); next.delete(fallbackDeleteId); return next; });
    }
  };

  // Прямое создание документа в СБИС без модального окна и подтверждения
  const handleDirectCreateSbisDocument = async (row) => {
    if (!row.ukey) {
      alert('Ошибка: отсутствует UKEY документа');
      return;
    }

    let tableName = 'СЧЕТФАКТ';
    if (row.description && row.description.includes('получен')) {
      tableName = 'СЧЕТФАКТПОСТ';
    } else if (row.type === 'Входящая') {
      tableName = 'СЧЕТФАКТПОСТ';
    }

    let yearMonth = null;
    if (row.date) {
      const dateParts = row.date.split('-');
      if (dateParts.length >= 2) {
        yearMonth = `${dateParts[0]}-${dateParts[1]}`;
      }
    }

    if (!yearMonth) {
      alert('Ошибка: не удалось определить период документа из даты ' + row.date);
      return;
    }

    setCreatingRows(prev => new Set(prev).add(row.id));
    try {
      const { createSbisDocument } = await import('../services/sbisApi');
      const result = await createSbisDocument(user.email, tableName, row.ukey, yearMonth, 'invoice');
      if (result.success) {
        const link = result.document.link;
        const conducted = !!result.document.conducted;
        // Update local UI state immediately
        setCreatedSbisLinks(prev => ({ ...prev, [row.id]: link }));
        if (conducted) {
          setConductedRows(prev => new Set(prev).add(row.id));
        }
        // Persist the link into tableRows and save to S3 cache
        setTableRows(prev => {
          const updated = prev.map(r =>
            r.id === row.id ? { ...r, sbisLink: link } : r
          );
          return updated;
        });
      } else {
        throw new Error(result.error || 'Неизвестная ошибка');
      }
    } catch (error) {
      debugError('Dashboard', 'Error creating SBIS document', error);
      alert(`❌ Ошибка создания документа в СБИС:\n\n${error.message}`);
    } finally {
      setCreatingRows(prev => { const next = new Set(prev); next.delete(row.id); return next; });
    }
  };

  // Обработчик клика по галочке создания СФ в СБИС
  const handleCreateInvoiceClick = async (row) => {
    debugInfo("Dashboard", "Opening CRD modal for invoice", { row });
    
    try {
      // Показываем индикатор загрузки
      setSelectedCrdData({ loading: true, ...row });
      setIsCrdModalOpen(true);
      setCrdModalTab('normal');
      
      // Определяем название таблицы на основе типа документа
      // По умолчанию используем СЧЕТФАКТ
      let tableName = 'СЧЕТФАКТ';
      
      // Пытаемся определить правильную таблицу из данных строки
      if (row.description && row.description.includes('получен')) {
        tableName = 'СЧЕТФАКТПОСТ';
      } else if (row.type === 'Входящая') {
        tableName = 'СЧЕТФАКТПОСТ';
      }
      
      // Извлекаем UKEY из данных строки
      let documentId = row.ukey;
      
      // Для объединённых документов UKEY может быть в duplicates
      if (!documentId && row.duplicates && Array.isArray(row.duplicates)) {
        const parsedDup = row.duplicates.find(d => d.ukey && (d.source === 'parsed' || d.source === 'crd'));
        if (parsedDup) {
          documentId = parsedDup.ukey;
          debugInfo("Dashboard", "Found UKEY in duplicates", { ukey: documentId });
        }
      }
      
      // Проверяем, есть ли данные из Turbo (CRD)
      const hasParsedData = row.hasParsed || (row.source === 'parsed') || (row.source === 'crd');
      
      // Если нет UKEY или нет данных из Parsed, это документ только из SBIS
      if (!documentId || !hasParsedData) {
        debugInfo("Dashboard", "No parsed data found", { 
          row, 
          documentId, 
          hasParsedData,
          ukey: row.ukey,
          hasParsed: row.hasParsed,
          source: row.source,
          duplicates: row.duplicates
        });
        setSelectedCrdData({
          ...row,
          error: 'Этот документ доступен только в СБИС. Данные из CRD файлов (Турбо) не найдены.',
          loading: false
        });
        return;
      }
      
      // Извлекаем год-месяц из даты для оптимизации поиска
      let yearMonth = null;
      if (row.date) {
        const dateParts = row.date.split('-');
        if (dateParts.length >= 2) {
          yearMonth = `${dateParts[0]}-${dateParts[1]}`;
        }
      }
      
      debugInfo("Dashboard", "Fetching CRD document", { 
        tableName, 
        documentId, 
        yearMonth,
        rowData: row
      });
      
      // Загружаем полные данные документа из CRD
      const result = await getCrdDocument(user.email, tableName, documentId, yearMonth);
      
      if (result.success && result.document) {
        debugInfo("Dashboard", "CRD document loaded successfully", result);
        
        // Обновляем модальное окно с полными данными
        setSelectedCrdData({
          ...row,
          crdData: result.document,
          tableName: result.tableName,
          yearMonth: result.yearMonth,
          loading: false
        });
      } else {
        throw new Error(result.error || 'Failed to load CRD document');
      }
      
    } catch (error) {
      debugError("Dashboard", "Error loading CRD document", error);
      
      // Показываем ошибку в модальном окне
      setSelectedCrdData({
        ...row,
        error: error.message || 'Не удалось загрузить данные из CRD',
        loading: false
      });
    }
  };

  // Загрузка свежих данных таблицы
  const loadInvoiceData = async (forceRefresh = false) => {
    try {
      debugInfo("Dashboard", "Loading invoice data", { dateFrom, dateTo, forceRefresh });
      
      // Импортируем API сервис
      const { getSbisInvoiceTable } = await import("../services/sbisApi");
      
      // Передаем даты в формате YYYY-MM-DD (backend ожидает именно этот формат)
      const result = await getSbisInvoiceTable(user.email, {
        dateFrom: dateFrom,
        dateTo: dateTo,
        forceRefresh: forceRefresh
      });
      
      // Проверяем, есть ли данные из CRD файлов
      if (result.success && result.data) {
        const hasCrdData = result.data.some(invoice => invoice.source === 'crd');
        
        // Сохраняем данные Регистра НДС
        if (result.vatRegister) {
          setVatRegister(result.vatRegister);
        }
        
        // Если нет данных из CRD, запускаем автоматический парсинг
        if (!hasCrdData) {
          console.log("⚠️ CRD данные не найдены, запускаем автоматический парсинг...");
          try {
            // parseAllCrdFiles(userEmail, format, force)
            const parseResult = await parseAllCrdFiles(user.email, 'json', false);
            const stats = parseResult.statistics || parseResult.summary || {};
            if (parseResult.success && (stats.tablesExtracted > 0 || stats.totalTables > 0)) {
              console.log("✅ Автоматический парсинг CRD завершен успешно");
              // Перезагружаем данные после парсинга
              const updatedResult = await getSbisInvoiceTable(user.email, {
                dateFrom: dateFrom,
                dateTo: dateTo
              });
              if (updatedResult.success && updatedResult.data) {
                if (updatedResult.vatRegister) {
                  setVatRegister(updatedResult.vatRegister);
                }
                processInvoiceData(updatedResult.data);
              }
              return;
            }
          } catch (parseError) {
            console.warn("⚠️ Автоматический парсинг CRD не удался:", parseError.message);
            // Продолжаем с данными которые есть
          }
        }
        
        processInvoiceData(result.data);
      }
    } catch (error) {
      debugError("Dashboard", "Error loading invoice data", error);
    }
  };

  // Вспомогательная функция для обработки данных счет-фактур
  const processInvoiceData = (invoices) => {
    console.log('🔄 processInvoiceData вызвана, обрабатывается записей:', invoices.length);

    const normalizeAmount = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num.toFixed(2) : String(value || '');
    };

    const getPaymentKey = (p) => [
      String(p?.id || ''),
      String(p?.number || ''),
      String(p?.date || ''),
      String(p?.amount || ''),
      String(p?.link || ''),
    ].join('|');

    const getPaymentBusinessKey = (p) => [
      normalizeDocNumber(p?.number || ''),
      String(p?.date || ''),
      normalizeAmount(p?.amount || 0),
    ].join('|');

    const dedupePayments = (payments) => {
      if (!Array.isArray(payments) || payments.length <= 1) return payments;
      const byBusinessKey = new Map();
      for (const p of payments) {
        const key = getPaymentBusinessKey(p);
        const prev = byBusinessKey.get(key);
        if (!prev) {
          byBusinessKey.set(key, p);
          continue;
        }

        // Для дублей одного и того же платежа оставляем более "сильную" запись
        // (предпочтительно проведённую, чтобы не дублировать строку с ☐/✅).
        if (!!p?.processed && !prev?.processed) {
          byBusinessKey.set(key, p);
        }
      }
      return Array.from(byBusinessKey.values());
    };
    
    // Преобразуем данные в формат таблицы
    const invoiceRows = invoices.map((invoice, index) => {
      // Извлекаем контрагента с учетом разных форматов данных
      let contractorName = 'Не указан';
      
      if (invoice.counterparty?.name) {
        // Для SBIS данных: counterparty.name может быть объектом
        if (typeof invoice.counterparty.name === 'string') {
          contractorName = invoice.counterparty.name;
        } else {
          // Для SBIS API: counterparty.name.СвЮЛ.Название или .Название
          contractorName = invoice.counterparty.name?.СвЮЛ?.Название || 
                         invoice.counterparty.name?.Название || 
                         'Не указан';
        }
      }
      
      return {
        id: invoice.id || index,
        originalId: invoice.originalId || null, // Оригинальный SBIS UUID для проведения
        ukey: invoice.ukey || null, // Добавляем UKEY для поиска в CRD
        date: invoice.date,
        contractor: contractorName,
        amount: invoice.amount || 0,
        vat20: invoice.vat20 || 0,
        vat22: invoice.vat22 || 0,
        vat: invoice.vat || 0,
        type: invoice.direction_text,
        direction: invoice.direction || '',
        status: invoice.status === 'unknown' ? 'Обработан' : invoice.status,
        number: invoice.number,
        description: invoice.description,
        source: invoice.source || 'sbis', // Добавляем источник данных
        sbisLink: invoice.sbisLink || '', // Добавляем ссылку на документ в СБИС
        contractorLink: invoice.contractorLink || '', // Ссылка на карточку контрагента в СБИС
        processed: invoice.processed || false, // Флаг проведённого документа в СБИС
        hasSbis: invoice.hasSbis, // Флаг наличия данных из СБИС
        hasParsed: invoice.hasParsed, // Флаг наличия данных из Турбо
        crdPosted: invoice.crdPosted ?? null, // Флаг проведённости в Турбо (false = не проведён)
        sources: invoice.sources, // Массив источников для объединённых записей
        sbisNumber: invoice.sbisNumber || null, // Оригинальный номер СБИС (для авансовых СФ с /АВ)
        docType: invoice.docType || null, // Тип документа СБИС (ФактураИсх, ДокОтгрИсх, ВходящийПлатеж, etc.)
        payments: dedupePayments(invoice.payments || null), // Привязанные платежи от покупателей (без дублей)
        isPayment: invoice.isPayment || false, // Это непривязанный платёж (отдельная строка)
        hasLinkedInvoice: invoice.hasLinkedInvoice || false, // К отгрузке привязана счет-фактура
        linkedInvoiceLink: invoice.linkedInvoiceLink || '', // Ссылка на счёт-фактуру
        linkedInvoiceNumber: invoice.linkedInvoiceNumber || '', // Номер авансового СФ
        linkedInvoiceProcessed: invoice.linkedInvoiceProcessed || false, // Статус проведения авансового СФ (при мерже по описанию)
        linkedInvoiceHasParsed: invoice.linkedInvoiceHasParsed || false, // Есть данные Турбо у авансового СФ (при мерже по описанию)
        linkedInvoiceDate: invoice.linkedInvoiceDate || '', // Дата авансового СФ (при мерже)
        inVatRegister: invoice.inVatRegister || false, // Найден в Регистре НДС
        inSalesBook: invoice.inSalesBook || false, // Найден в Книге продаж
        inPurchaseBook: invoice.inPurchaseBook || false, // Найден в Книге покупок
        vatRateDisplay: invoice.vatRateDisplay || null, // Ставка НДС: оплата/аванс.СФ/отгрузка
        closingDoc: invoice.closingDoc || null // Закрывающая реализация для авансовых СФ
      };
    });
    
    // Разворачиваем привязанные платежи: один платёж = одна строка таблицы
    const expandedRowsRaw = invoiceRows.flatMap((row) => {
      if (!Array.isArray(row.payments) || row.payments.length <= 1) {
        return [row];
      }

      return row.payments.map((payment, idx) => ({
        ...row,
        id: `${row.id}_p${idx + 1}`,
        splitByPayment: true,
        payments: [payment],
      }));
    });

    // Дополнительная защита от дублей строк при одинаковых исходных данных
    const seenRows = new Set();
    const expandedRows = expandedRowsRaw.filter((row) => {
      const p = Array.isArray(row.payments) && row.payments.length === 1 ? row.payments[0] : null;
      const rowKey = [
        String(row.number || ''),
        String(row.date || ''),
        String(row.contractor || ''),
        String(row.amount || ''),
        String(row.sbisLink || ''),
        String(row.linkedInvoiceLink || ''),
        String(row.linkedInvoiceNumber || ''),
        String(row.docType || ''),
        String(row.type || ''),
        String(!!row.isPayment),
        p ? getPaymentKey(p) : '',
      ].join('||');

      if (seenRows.has(rowKey)) return false;
      seenRows.add(rowKey);
      return true;
    });

    setTableRows(expandedRows);
    setDataSource('fresh');
    debugInfo("Dashboard", "Invoice data loaded successfully", {
      sourceCount: invoiceRows.length,
      expandedCount: expandedRows.length
    });

    // Сбрасываем createdSbisLinks для строк, у которых нет sbisLink в свежих данных.
    // Это нужно чтобы ✅ не оставалась после удаления документа из СБИС.
    setCreatedSbisLinks(prev => {
      const next = { ...prev };
      expandedRows.forEach(r => {
        if (!r.sbisLink && next[r.id]) {
          delete next[r.id];
        }
      });
      return next;
    });

  };

  useEffect(() => {
    console.log("⚡ Dashboard useEffect triggered");
    console.log("🔍 User object in useEffect:", user);
    console.log("🔍 User email:", user?.email);
    console.log("🔍 Data loaded flag:", isDataLoaded);
    
    if (user?.email && !isDataLoaded) {
      // Инициализируем статистику
      setStats({
        totalAccounts: 247,
        activeContracts: 156,
        pendingRequests: 23,
        completedTasks: 89,
      });
      
      // Загружаем данные через Lambda (кэш → полный запрос как fallback)
      (async () => {
        try {
          const { getSbisInvoiceTableCache } = await import("../services/sbisApi");
          const cached = await getSbisInvoiceTableCache(user.email, { dateFrom, dateTo });
          if (cached.success && cached.data && cached.data.length > 0) {
            if (cached.vatRegister && Array.isArray(cached.vatRegister)) {
              setVatRegister(cached.vatRegister);
            }
            processInvoiceData(cached.data);
          } else {
            await loadInvoiceData();
          }
        } catch (e) {
          await loadInvoiceData();
        }
      })();
      setIsDataLoaded(true);
      console.log("✅ Dashboard ready, loading data from Lambda");
    } else if (!user?.email) {
      console.warn("⚠️ User not logged in or email missing");
      // Сбрасываем данные при выходе из системы
      setTableRows([]);
      setDataSource('');
      setSelectedRows([]);
      setIsDataLoaded(false);
    }
  }, [user?.email]); // Перезагружаем данные при смене пользователя

  const handleUploadComplete = async (response) => {
    debugInfo("Dashboard", "File uploaded successfully", response);
    const fileName = (response?.fileName || '').toLowerCase();
    if (fileName.endsWith('.xlsx') || response?.xlsxParsed) {
      // Быстро обновляем vatRegister (только регистр НДС, ~1-2 сек)
      try {
        const { getVatRegister } = await import('../services/sbisApi');
        const vrResult = await getVatRegister(user.email);
        if (vrResult?.vatRegister) {
          setVatRegister(vrResult.vatRegister);
          console.log('✅ vatRegister обновлён после загрузки XLSX:', vrResult.vatRegister.length, 'записей');
        }
      } catch (vrErr) {
        console.warn('⚠️ Не удалось обновить vatRegister:', vrErr.message);
      }
      // Фоновый полный перерасчёт данных таблицы
      loadInvoiceData(true);
    } else if (fileName.endsWith('.crd')) {
      // Если загружен CRD — автоматически парсим и перезагружаем данные
      try {
        setRefreshing(true);
        console.log('⚡ Автопарсинг загруженного CRD файла...');
        await parseAllCrdFiles(user.email, 'json', true);
        console.log('✅ CRD файл распарсен');
        await loadInvoiceData();
      } catch (err) {
        console.error('❌ Ошибка автопарсинга CRD:', err);
      } finally {
        setRefreshing(false);
      }
    }
  };

  // Синхронизация SBIS данных
  const handleRefreshSbis = async () => {
    debugInfo("Dashboard", "Refreshing SBIS data");
    
    if (!user || !user.email) {
      console.error("❌ User not authenticated:", { user, email: user?.email });
      alert("Ошибка: пользователь не авторизован");
      return;
    }
    
    // Проверяем, выбраны ли даты
    if (!dateFrom || !dateTo) {
      alert("⚠️ Укажите период для синхронизации");
      return;
    }
    
    try {
      setRefreshing(true);
      
      // Импортируем API сервис
      const { syncSbisDocumentsByDate } = await import("../services/sbisApi");
      
      // Вызываем синхронизацию с фильтром по датам
      console.log("📡 Синхронизация документов из SBIS...");
      const result = await syncSbisDocumentsByDate(user.email, dateFrom, dateTo);
      
      debugInfo("Dashboard", "SBIS documents synced successfully", result);
      
      // Формируем детальное сообщение
      const { data } = result;
      
      let message = `✅ СБИС: Синхронизация завершена!\n\n`;
      message += `📅 Период: ${dateFrom} — ${dateTo}\n\n`;
      message += `📊 Статистика:\n`;
      message += `  • Всего документов: ${data.totalDocuments}\n\n`;
      
      if (data.statistics) {
        message += `📋 По типам:\n`;
        for (const [type, stat] of Object.entries(data.statistics)) {
          message += `  • ${stat.description}: ${stat.total}\n`;
        }
      }
      
      if (data.savedFiles && data.savedFiles.length > 0) {
        message += `\n💾 Сохранено файлов: ${data.savedFiles.length}\n`;
      }
      
      console.log(message);
      
      // После успешной синхронизации загружаем обработанные данные (форсированно, минуя кэш)
      await loadInvoiceData(true);
      
    } catch (error) {
      debugError("Dashboard", "Error syncing SBIS documents", error);
      console.error("❌ Ошибка при синхронизации СБИС:", error.message);
      alert(
        "❌ Ошибка при синхронизации СБИС\n\n" +
        error.message + "\n\n" +
        "Проверьте:\n" +
        "• Настройки SBIS в разделе настроек\n" +
        "• Правильность указанного периода\n" +
        "• Подключение к серверу"
      );
    } finally {
      setRefreshing(false);
    }
  };

  // Парсинг CRD файлов (Турбо)
  const handleRefreshTurbo = async () => {
    debugInfo("Dashboard", "Refreshing Turbo (parsed CRD) data");
    
    if (!user || !user.email) {
      console.error("❌ User not authenticated:", { user, email: user?.email });
      alert("Ошибка: пользователь не авторизован");
      return;
    }
    
    try {
      setRefreshing(true);
      
      console.log("⚡ Парсинг CRD файлов (Турбо)...");
      
      // Вызываем парсинг всех CRD файлов с force=true для принудительного парсинга
      // parseAllCrdFiles(userEmail, format, force)
      const result = await parseAllCrdFiles(user.email, 'json', true); // format='json', force=true
      
      debugInfo("Dashboard", "CRD files parsed successfully", result);
      
      // Формируем сообщение
      let message = `✅ Турбо: Парсинг завершён!\n\n`;
      
      if (result.success) {
        const stats = result.statistics || result.summary || {};
        message += `📊 Статистика:\n`;
        message += `  • Всего файлов: ${stats.filesProcessed || stats.totalFiles || 0}\n`;
        message += `  • Обработано: ${stats.filesSuccess || stats.filesProcessed || 0}\n`;
        message += `  • Пропущено: ${stats.filesSkipped || 0}\n`;
        message += `  • Таблиц извлечено: ${stats.totalTables || stats.tablesExtracted || 0}\n`;
        
        if (result.errors && result.errors.length > 0) {
          message += `\n⚠️ Ошибок: ${result.errors.length}\n`;
        }
      } else {
        message = `❌ Ошибка парсинга CRD файлов\n\n${result.message || 'Неизвестная ошибка'}`;
      }
      
      console.log(message);
      // alert(message); // Убрали модалку после парсинга
      
      // После успешного парсинга загружаем обработанные данные
      await loadInvoiceData();
      
    } catch (error) {
      debugError("Dashboard", "Error parsing CRD files", error);
      alert(
        "❌ Ошибка при парсинге CRD файлов\n\n" +
        error.message + "\n\n" +
        "Проверьте:\n" +
        "• Наличие CRD файлов в хранилище\n" +
        "• Подключение к серверу"
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard" data-testid="dashboard">
      <div className="dashboard-header" data-testid="dashboard-header">
        <div className="dashboard-header-left">
          <button
            className="btn-settings"
            onClick={() => setIsSettingsOpen(true)}
            title="Настройки"
            data-testid="btn-settings"
          >
            ⚙️
          </button>
          <button
            className="btn-refresh-sbis"
            onClick={refreshing ? undefined : handleRefreshSbis}
            title="Синхронизировать данные из СБИС"
            data-testid="btn-refresh-sbis"
            disabled={refreshing}
            style={{ cursor: refreshing ? 'default' : 'pointer' }}
          >
            {refreshing ? '…' : '↻'}
          </button>
          <input
            type="file"
            ref={uploadInputRef}
            accept=".crd,.xlsx,.xml"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) {
                setPendingUploadFiles(files);
              }
              e.target.value = '';
            }}
          />
          <button
            className="btn-upload"
            onClick={() => uploadInputRef.current && uploadInputRef.current.click()}
            title="Загрузить файл"
            data-testid="btn-upload"
          >
            📤
          </button>
          <div className="date-filter" data-testid="date-filter">
            <label>
              Дата с:
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="ДД.ММ.ГГГГ"
                data-testid="input-date-from"
              />
            </label>
            <label>
              Дата по:
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="ДД.ММ.ГГГГ"
                data-testid="input-date-to"
              />
            </label>
            <button className="btn-filter" onClick={handleFilter} data-testid="btn-filter">
              Фильтровать
            </button>
          </div>
        </div>
        {onLogout && (
          <button className="btn btn-logout" onClick={onLogout} data-testid="btn-logout">
            Выйти
          </button>
        )}
      </div>

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      {pendingUploadFiles && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 1000,
          background: 'white', borderRadius: '12px', padding: '16px 20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.18)', minWidth: '320px', maxWidth: '480px'
        }}>
          <button
            onClick={() => setPendingUploadFiles(null)}
            style={{ position: 'absolute', top: '10px', right: '12px', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#999', lineHeight: 1 }}
          >×</button>
          <FileUpload
            initialFiles={pendingUploadFiles}
            onUploadComplete={handleUploadComplete}
            onClose={() => setPendingUploadFiles(null)}
          />
        </div>
      )}

      {/* Таблица с чекбоксами и нужными столбцами */}
        <div className="dashboard-table-section">
          <div className="table-header">
            <div className="table-search">
              <input
                type="text"
                placeholder="Поиск по номеру или контрагенту..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                data-testid="input-search"
              />
            </div>
            <h2>Документы</h2>
            {(contractorFilter || numberFilter || dateFilter || amountFilter !== null) && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: '12px', flexWrap: 'wrap' }}>
                {numberFilter && (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e8f0fe', color: '#1a73e8', padding: '4px 10px', borderRadius: '16px', fontSize: '13px', cursor: 'pointer' }}
                    onClick={() => setNumberFilter('')}
                    title="Сбросить фильтр по номеру"
                  >
                    №{numberFilter} ✕
                  </span>
                )}
                {dateFilter && (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e8f0fe', color: '#1a73e8', padding: '4px 10px', borderRadius: '16px', fontSize: '13px', cursor: 'pointer' }}
                    onClick={() => setDateFilter('')}
                    title="Сбросить фильтр по дате"
                  >
                    {dateFilter} ✕
                  </span>
                )}
                {contractorFilter && (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e8f0fe', color: '#1a73e8', padding: '4px 10px', borderRadius: '16px', fontSize: '13px', cursor: 'pointer' }}
                    onClick={() => setContractorFilter('')}
                    title="Сбросить фильтр по контрагенту"
                  >
                    {contractorFilter} ✕
                  </span>
                )}
                {amountFilter !== null && (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e8f0fe', color: '#1a73e8', padding: '4px 10px', borderRadius: '16px', fontSize: '13px', cursor: 'pointer' }}
                    onClick={() => setAmountFilter(null)}
                    title="Сбросить фильтр по сумме"
                  >
                    {amountFilter.toLocaleString('ru-RU')} ✕
                  </span>
                )}
              </span>
            )}
          </div>
          
          {/* Локальный индикатор загрузки таблицы */}
          {refreshing && (
            <div className="table-loading-overlay">
              <div className="loading-spinner"></div>
              <p>Обновление данных...</p>
            </div>
          )}
          
          <table className="dashboard-table" data-testid="documents-table">
            <thead>
              <tr data-testid="table-header-row">
                <th style={{ verticalAlign: 'middle', textAlign: 'center', padding: '4px 2px', width: '24px', minWidth: '24px' }}>
                  <span
                    onClick={() => setPaymentDirectionFilter(f => f === 'all' ? 'outgoing' : f === 'outgoing' ? 'incoming' : 'all')}
                    title={paymentDirectionFilter === 'all' ? 'Все платежи (кликни для фильтра)' : paymentDirectionFilter === 'outgoing' ? 'Только исходящие' : 'Только входящие'}
                    style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: paymentDirectionFilter === 'incoming' ? '#dc3545' : paymentDirectionFilter === 'outgoing' ? '#28a745' : '#adb5bd', cursor: 'pointer', boxShadow: paymentDirectionFilter !== 'all' ? '0 0 0 2px #fff, 0 0 0 3px currentColor' : 'none', verticalAlign: 'middle' }}
                  ></span>
                </th>
                <th style={{ verticalAlign: 'middle', textAlign: 'center', padding: '4px 2px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }} title="Сортировать по дате оплаты" onClick={() => handleSort('sbisPaymentDate')}>Оплата{sortConfig.key === 'sbisPaymentDate' ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}</span>
                </th>
                <th style={{ verticalAlign: 'middle', textAlign: 'center', padding: '4px 0', width: '20px', minWidth: '20px' }} title="Сравнение дат: Оплата и Аванс.СФ">⇄</th>
                <th style={{ verticalAlign: 'middle', textAlign: 'center', padding: '4px 2px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }} title="Сортировать по дате аванса" onClick={() => handleSort('sbisAdvanceDate')}>Аванс.СФ{sortConfig.key === 'sbisAdvanceDate' ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}</span>
                </th>
                <th style={{ verticalAlign: 'middle', textAlign: 'center', padding: '4px 0', width: '20px', minWidth: '20px' }} title="Сравнение дат: Аванс.СФ и Отгрузка">⇄</th>
                <th style={{ verticalAlign: 'middle', textAlign: 'center', padding: '4px 2px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }} title="Сортировать по дате отгрузки" onClick={() => handleSort('sbisShipmentDate')}>Отгрузка{sortConfig.key === 'sbisShipmentDate' ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}</span>
                </th>
                <th 
                  className={`sortable ${sortConfig.key === 'contractor' ? 'sorted' : ''}`}
                  onClick={() => handleSort('contractor')}
                >
                  Контрагент
                  {sortConfig.key === 'contractor' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th 
                  className={`sortable ${sortConfig.key === 'amount' ? 'sorted' : ''}`}
                  onClick={() => handleSort('amount')}
                >
                  Сумма
                  {sortConfig.key === 'amount' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th
                  title="НДС: сумма(ставка) по оплате / авансовому СФ / реализации"
                  style={{ whiteSpace: 'nowrap', textAlign: 'center', fontSize: '11px' }}
                >
                  НДС
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTableRows.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    {tableRows.length === 0 ? (
                      <div>
                        <p style={{ fontSize: '16px', marginBottom: '10px' }}>📋 Нет данных для отображения</p>
                        <p style={{ fontSize: '14px' }}>Нажмите кнопку "🔄 Обновить" для загрузки документов из СБИС</p>
                      </div>
                    ) : (
                      <p>Ничего не найдено по запросу "{searchQuery}"</p>
                    )}
                  </td>
                </tr>
              ) : (
                filteredTableRows.map((row) => {
                  // Определяем, есть ли данные из разных источников
                  const hasParsed = row.source === 'parsed' || row.hasParsed || 
                                   (row.sources && row.sources.includes('parsed')) ||
                                   (row.sources && row.sources.includes('crd'));
                  const hasSbis = row.source === 'sbis' || row.hasSbis || 
                                 (row.sources && row.sources.includes('sbis'));
                  const sbisLink = row.sbisLink || '';
                  const isDeleted = /ПР$/i.test(String(row.number || ''));

                  // Зелёный фон: сопоставление с Регистром НДС выполняется на стороне фронта
                  const _isAdvanceRow = /\/АВ/i.test(row.sbisNumber || '') || /\/АВ/i.test(row.number || '') || /ПР$/i.test(row.number || '') || /ПР$/i.test(row.sbisNumber || '');
                  const _isPaymentRow = row.isPayment || row.docType === 'ВходящийПлатеж' || row.docType === 'ИсходящийПлатеж' || row.docType === 'БанковскаяВыписка';
                  const _rowNum = (row.number || '').trim().toUpperCase();
                  const _rowDate = (row.date || '').trim();
                  // Для сложных номеров (содержат нецифровые символы: 07_26ПР, 03_2026Д) —
                  // ищем только по НОМЕРУ, т.к. дата в реестре НДС может отличаться от даты документа.
                  // Для чисто числовых номеров (216, 7) — строгое НОМЕР|ДАТА.
                  const _isComplexNum = (n) => /\D/.test(n);
                  const _lookupKey = _isComplexNum(_rowNum) ? _rowNum : (_rowDate ? `${_rowNum}|${_rowDate}` : _rowNum);
                  // Для авансовых СФ с закрывающей реализацией: проверяем также номер closingDoc
                  const _closingNum  = String(row.closingDoc?.number || '').trim().toUpperCase();
                  const _closingDate = String(row.closingDoc?.date || '').trim();
                  const _closingKey  = _isComplexNum(_closingNum) ? _closingNum : (_closingDate ? `${_closingNum}|${_closingDate}` : _closingNum);
                  // Если vatRegister ещё не загружен — используем флаги из данных сервера (кэш)
                  const _isInSalesBook = !_isPaymentRow && (
                    vatRegisterLookup.salesBook.size > 0
                      ? (vatRegisterLookup.salesBook.has(_lookupKey) ||
                         (_closingNum && vatRegisterLookup.salesBook.has(_closingKey)))
                      : (!!row.inSalesBook || !!row.closingDoc?.inSalesBook)
                  );
                  const _isInPurchaseBook = !_isPaymentRow && (
                    vatRegisterLookup.purchaseBook.size > 0
                      ? (vatRegisterLookup.purchaseBook.has(_lookupKey) ||
                         (_closingNum && vatRegisterLookup.purchaseBook.has(_closingKey)))
                      : (!!row.inPurchaseBook || !!row.closingDoc?.inPurchaseBook)
                  );
                  const isOutgoingShipmentInSalesBook = !_isAdvanceRow && _isInSalesBook;
                  const isIncomingReceiptInPurchaseBook = !_isAdvanceRow && _isInPurchaseBook;

                  const docId = `${row.number || row.id}_${row.date}`;
                  const _vatPartsCheck = (row.vatRateDisplay || '-/-/-').split('/');
                  const _isNoVatRow = _vatPartsCheck.every(p => p === '-' || p === '0')
                    && !isOutgoingShipmentInSalesBook && !isIncomingReceiptInPurchaseBook;
                  return (
                    <tr key={row.id} className={selectedRows.includes(row.id) ? "selected" : ""} style={_isNoVatRow ? { backgroundColor: '#f0f0f0' } : {}} data-testid={`row-${docId}`}>
                      {/* --- СБИС: 3 столбца (Оплата / Авансовый СФ / Отгрузка), Т внутри Аванс.СФ и Отгрузка --- */}
                      {(() => {
                        const isSplitPaymentRow = !!row.splitByPayment;
                        const splitPaymentDate = isSplitPaymentRow ? String(row.payments?.[0]?.date || '') : '';
                        const splitDocDate = isSplitPaymentRow ? String(row.date || '') : '';
                        const showSplitPaymentFlow = isSplitPaymentRow && splitPaymentDate !== '' && splitDocDate !== '' && splitPaymentDate < splitDocDate;
                        const isAdvance = /\/АВ/i.test(row.sbisNumber || '') || /\/АВ/i.test(row.number || '') || /ПР$/i.test(row.number || '') || /ПР$/i.test(row.sbisNumber || '');
                        const isPaymentDoc = row.isPayment || row.docType === 'ВходящийПлатеж' || row.docType === 'ИсходящийПлатеж' || row.docType === 'БанковскаяВыписка';
                        // sbisCategory: which column this row's own SBIS link goes into
                        const sbisCategory = isAdvance
                          ? 'advance'
                          : (isSplitPaymentRow && !showSplitPaymentFlow)
                            ? 'payment'
                            : (isPaymentDoc ? 'payment' : 'shipment');
                        const showOwnPaymentDoc = sbisCategory === 'payment' && !isSplitPaymentRow;

                        // Linked advance invoice (for shipment rows that close an advance SF)
                        const linkedInvNum = row.linkedInvoiceNumber || '';
                        const linkedIsAdvance = /\/АВ/i.test(linkedInvNum) || /ПР$/i.test(linkedInvNum);
                        const showLinkedAdvance = sbisCategory === 'shipment' && linkedIsAdvance && !!row.linkedInvoiceLink;

                        const paymentDateForCompare = (() => {
                          if (showOwnPaymentDoc && row.date) return row.date;
                          if (Array.isArray(row.payments) && row.payments.length > 0) {
                            const sortedPayments = [...row.payments].sort((a, b) =>
                              String(a?.date || '').localeCompare(String(b?.date || ''))
                            );
                            return String(sortedPayments[0]?.date || '');
                          }
                          return '';
                        })();

                        const advanceDateForCompare = sbisCategory === 'advance'
                          ? String(row.date || '')
                          : (showLinkedAdvance ? String(row.linkedInvoiceDate || '') : '');

                        const shipmentDateForCompare = sbisCategory === 'shipment'
                          ? String(row.date || '')
                          : (sbisCategory === 'advance' ? String(row.closingDoc?.date || '') : '');

                        const hasAdvanceDate = parseDateToTs(advanceDateForCompare) != null;
                        const paymentVsAdvanceSign = hasAdvanceDate
                          ? compareDateSymbol(paymentDateForCompare, advanceDateForCompare)
                          : '';
                        const advanceVsShipmentSign = hasAdvanceDate
                          ? compareDateSymbol(advanceDateForCompare, shipmentDateForCompare)
                          : compareDateSymbol(paymentDateForCompare, shipmentDateForCompare);

                        // Render SBIS cell content (link + status + delete)
                        const renderSbisCell = (active, { showDelete = true, showTurbo = false } = {}) => {
                          if (!active) return '';
                          const turboEl = showTurbo && hasParsed ? (
                            <span 
                              style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', color: row.crdPosted === false ? '#e6a800' : '#27ae60' }} 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCreateInvoiceClick(row);
                              }}
                              title={row.crdPosted === false ? "Не проведён в Турбо (CRD)" : "Показать данные из CRD"}
                              data-testid={`btn-show-crd-${docId}`}
                            >Т</span>
                          ) : null;
                          if (hasSbis && sbisLink) {
                            return (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
                                {turboEl}
                                <a href={sbisLink} target="_blank" rel="noopener noreferrer"
                                  style={{ textDecoration: 'none', fontSize: '21px', cursor: 'pointer' }}
                                  title="Открыть документ в СБИС" onClick={(e) => e.stopPropagation()}
                                  data-testid={`link-sbis-${docId}`}
                                >⧉</a>
                                {conductingRows.has(row.id) ? (
                                  <span style={{ fontSize: '13px' }} title="Изменение проведения...">…</span>
                                ) : (
                                  <input
                                    type="checkbox"
                                    checked={!!row.processed}
                                    readOnly
                                    style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#28a745' }}
                                    title={row.processed ? 'Снять проведение в СБИС' : 'Провести документ в СБИС'}
                                    onClick={(e) => { e.stopPropagation(); handleToggleSbisDocumentConduct(row, !row.processed); }}
                                    data-testid={`btn-sbis-toggle-conduct-${docId}`}
                                  />
                                )}
                                {showDelete && (deletingRows.has(row.id) ? (
                                  <span style={{ fontSize: '13px' }} title="Удаление...">…</span>
                                ) : (
                                  <span style={{ fontSize: '20px', cursor: 'pointer', opacity: 0.6 }} title="Удалить документ из СБИС"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteSbisDocument(row); }}
                                    data-testid={`btn-sbis-delete-${docId}`}
                                  >✕</span>
                                ))}
                              </div>
                            );
                          }
                          if (hasSbis) return <>{turboEl}<input type="checkbox" checked readOnly style={{ width: '16px', height: '16px', accentColor: '#28a745' }} /></>;
                          if (hasParsed) {
                            if (creatingRows.has(row.id)) return <>{turboEl}<span style={{ fontSize: '16px' }} title="Создание...">…</span></>;
                            if (createdSbisLinks[row.id]) {
                              return (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px' }}>
                                  {turboEl}
                                  <a href={createdSbisLinks[row.id]} target="_blank" rel="noopener noreferrer"
                                    style={{ textDecoration: 'none', fontSize: '21px', cursor: 'pointer' }}
                                    title="Открыть документ в СБИС" onClick={(e) => e.stopPropagation()}
                                  >⧉</a>
                                  <input type="checkbox" checked={conductedRows.has(row.id)} readOnly style={{ width: '16px', height: '16px', accentColor: '#28a745' }} />
                                  {showDelete && (deletingRows.has(row.id) ? (
                                    <span style={{ fontSize: '13px' }} title="Удаление...">…</span>
                                  ) : (
                                    <span style={{ fontSize: '20px', cursor: 'pointer', opacity: 0.6 }} title="Удалить документ из СБИС"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteSbisDocument(row); }}
                                    >✕</span>
                                  ))}
                                </div>
                              );
                            }
                            return (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px' }}>
                                {turboEl}
                                <span style={{ fontSize: '16px', cursor: 'pointer' }} title="Создать СФ в СБИС"
                                  onClick={(e) => { e.stopPropagation(); handleDirectCreateSbisDocument(row); }}
                                  data-testid={`btn-sbis-create-${docId}`}
                                >+</span>
                              </div>
                            );
                          }
                          if (turboEl) return turboEl;
                          return '';
                        };

                        // Render payment cell (linked payments for non-payment rows)
                        const renderPaymentCell = () => {
                          if (row.payments && row.payments.length > 0) {
                            const sortedPayments = [...row.payments].sort((a, b) =>
                              String(a?.date || '').localeCompare(String(b?.date || ''))
                            );
                            const primaryPayment = sortedPayments[0] || null;
                            const dates = [...new Set(sortedPayments.map(p => p.date).filter(Boolean))];
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
                                  {primaryPayment?.link ? (
                                    <a href={primaryPayment.link} target="_blank" rel="noopener noreferrer"
                                      style={{ textDecoration: 'none', fontSize: '21px', cursor: 'pointer' }}
                                      title={`Открыть платёж №${primaryPayment.number} в СБИС`}
                                      onClick={(e) => e.stopPropagation()}
                                    >⧉</a>
                                  ) : <span style={{ fontSize: '14px' }}>₽</span>}
                                  <input
                                    type="checkbox"
                                    checked={row.payments.every(p => p.processed)}
                                    readOnly
                                    style={{ width: '16px', height: '16px', accentColor: '#28a745' }}
                                  />
                                </div>
                                {dates.length > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                    {dates.map((d, idx) => (
                                      <span key={`${d}_${idx}`} style={{ fontSize: '10px', color: '#888', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                        {d}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          // Нет привязанной оплаты — красный ⧉
                          return (
                            <span
                              style={{ fontSize: '21px', color: '#dc3545', opacity: 0.75 }}
                              title="Оплата не привязана в СБИС"
                            >⧉</span>
                          );
                        };

                        const cellStyle = { textAlign: 'center', padding: '4px', borderLeft: '1px solid #e9ecef', minWidth: '58px' };

                        // Определяем цвет кружка: по платежу (если есть), иначе по документу
                        const hasPaymentInRow = isPaymentDoc || (Array.isArray(row.payments) && row.payments.length > 0);
                        let circleIsIncoming;
                        if (isPaymentDoc) {
                          // Платёжный документ — напрямую из docType
                          circleIsIncoming = row.docType === 'ВходящийПлатеж';
                        } else if (hasPaymentInRow) {
                          // Есть привязанные платежи — исходящий документ (реализация) = входящий платёж от покупателя
                          circleIsIncoming = row.type !== 'Входящая';
                        } else {
                          // Нет платежа — по типу документа отгрузки
                          circleIsIncoming = row.type !== 'Входящая';
                        }

                        const isIncomingPayment = row.type === 'Входящая' || row.docType === 'ВходящийПлатеж';
                        return (
                          <>
                            {/* Направление платежа */}
                            <td style={{ textAlign: 'center', padding: '2px 0', width: '20px', minWidth: '20px', borderLeft: '1px solid #e9ecef' }}
                                title={circleIsIncoming ? 'Входящий платёж' : 'Исходящий платёж'}>
                              <span style={{ display: 'inline-block', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: circleIsIncoming ? '#28a745' : '#dc3545' }}></span>
                            </td>
                            {/* Оплата */}
                            <td style={{ ...cellStyle }}
                                title={row.payments ? row.payments.map(p => `Платёж №${p.number} от ${p.date} на ${parseFloat(p.amount).toLocaleString('ru-RU')} руб.${p.processed ? ' (проведён)' : ''}`).join('\n') : isPaymentDoc ? 'Платёжный документ' : ''}>
                              {showOwnPaymentDoc ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                  {renderSbisCell(true, { showDelete: false })}
                                  {row.date && <span style={{ fontSize: '10px', color: '#888', lineHeight: 1, whiteSpace: 'nowrap' }}>{row.date}</span>}
                                </div>
                              ) : renderPaymentCell()}
                            </td>
                            <td
                              style={{ ...cellStyle, minWidth: '20px', width: '20px', padding: '2px 0', fontWeight: 700, color: '#546E7A' }}
                              title={paymentVsAdvanceSign ? `Сравнение дат: ${paymentDateForCompare} ${paymentVsAdvanceSign} ${advanceDateForCompare}` : 'Нет данных для сравнения дат'}
                            >
                              {paymentVsAdvanceSign}
                            </td>
                            {/* Авансовый СФ */}
                            {(() => {
                              // Определяем номер+дату авансового СФ в этой ячейке:
                              //   - если строка сама является авансом → row.number + row.date
                              //   - если строка — отгрузка со связанным авансом → linkedInvNum + linkedInvoiceDate
                              const advNum = sbisCategory === 'advance'
                                ? (row.number || '').trim().toUpperCase()
                                : (showLinkedAdvance ? linkedInvNum.trim().toUpperCase() : '');
                              const advDate = sbisCategory === 'advance'
                                ? (row.date || '').trim()
                                : (showLinkedAdvance ? (row.linkedInvoiceDate || '').trim() : '');
                              const advLookupKey = _isComplexNum(advNum)
                                ? advNum
                                : (advDate ? `${advNum}|${advDate}` : advNum);
                              const advInSalesBook = advNum && (
                                vatRegisterLookup.salesBook.size > 0
                                  ? vatRegisterLookup.salesBook.has(advLookupKey)
                                  : (sbisCategory === 'advance' ? !!row.inSalesBook : false)
                              );
                              const advInPurchaseBook = advNum && (
                                vatRegisterLookup.purchaseBook.size > 0
                                  ? vatRegisterLookup.purchaseBook.has(advLookupKey)
                                  : (sbisCategory === 'advance' ? !!row.inPurchaseBook : false)
                              );
                              const showAdv = sbisCategory === 'advance' || showLinkedAdvance;
                              const advBg = showAdv && (advInSalesBook || advInPurchaseBook)
                                ? (advInSalesBook && advInPurchaseBook
                                    ? 'linear-gradient(to right, #d4edda 50%, #d4edda 50%)'
                                    : advInSalesBook
                                      ? 'linear-gradient(to right, #d4edda 50%, transparent 50%)'
                                      : 'linear-gradient(to right, transparent 50%, #d4edda 50%)')
                                : undefined;
                              return (
                            <td style={{ ...cellStyle, background: advBg }}
                                title={sbisCategory === 'advance' ? 'Авансовый счёт-фактура' : showLinkedAdvance ? `Авансовый СФ №${linkedInvNum}` : ''}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                {renderSbisCell(sbisCategory === 'advance', { showTurbo: sbisCategory === 'advance' })}
                                {sbisCategory === 'advance' && (() => {
                                  const turboNum = row.number || '';
                                  const sbisNum = row.sbisNumber || '';
                                  const hasArrow = turboNum && sbisNum && turboNum !== sbisNum;
                                  const label = hasArrow ? `${turboNum}=>${sbisNum}` : (sbisNum || turboNum);
                                  return (
                                    <span style={{ fontSize: '10px', color: '#888', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                      {label}{row.date ? ` ${row.date}` : ''}
                                    </span>
                                  );
                                })()}
                                {showLinkedAdvance && (() => {
                                  const linkedKey = normalizeDocNumber(linkedInvNum);
                                  const linkedStateKey = getLinkedAdvanceStateKey(row, linkedInvNum);
                                  const advRow = tableRows.find(r =>
                                    (linkedKey && (normalizeDocNumber(r.number) === linkedKey || normalizeDocNumber(r.sbisNumber) === linkedKey)) ||
                                    (row.linkedInvoiceLink && r.sbisLink === row.linkedInvoiceLink)
                                  );
                                  const advHasParsed = advRow?.hasParsed || !!row.linkedInvoiceHasParsed;
                                  // advProcessed: нужен статус именно ФактураВх (авансовой СФ), а не ДокОтгрВх.
                                  // Если advRow — это мёрж ДокОтгрВх+ФактураВх (linkedInvoiceLink совпадает
                                  // с тем, что мы показываем как ссылку на СФ), берём linkedInvoiceProcessed.
                                  const advProcessed = advRow
                                    ? (advRow.linkedInvoiceLink && row.linkedInvoiceLink && advRow.linkedInvoiceLink === row.linkedInvoiceLink
                                        ? !!advRow.linkedInvoiceProcessed
                                        : !!advRow.processed)
                                    : (linkedAdvanceConducted[linkedStateKey] !== undefined ? !!linkedAdvanceConducted[linkedStateKey] : !!row.linkedInvoiceProcessed);
                                  const advCrdPosted = advRow ? advRow.crdPosted : undefined;
                                  const advDate = advRow?.date || '';
                                  const fallbackConductId = `linked_${row.id || linkedInvNum || ''}`;
                                  const fallbackDeleteId = `linked_del_${row.id || linkedInvNum || ''}`;
                                  const canToggleLinkedAdvance = !!advRow || !!extractGuidFromSbisLink(row.linkedInvoiceLink);
                                  const canDeleteLinkedAdvance = !!advRow || !!extractGuidFromSbisLink(row.linkedInvoiceLink);
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '3px' }}>
                                        {advHasParsed && (
                                          <span
                                            style={{ fontWeight: 'bold', fontSize: '12px', color: advCrdPosted === false ? '#e6a800' : '#27ae60', cursor: 'default' }}
                                            title={advCrdPosted === false ? "Не проведён в Турбо (CRD)" : "Документ в Турбо (CRD)"}
                                          >Т</span>
                                        )}
                                        <a href={row.linkedInvoiceLink} target="_blank" rel="noopener noreferrer"
                                          style={{ textDecoration: 'none', fontSize: '21px' }}
                                          title={`Открыть авансовый СФ №${linkedInvNum} в СБИС`}
                                          onClick={(e) => e.stopPropagation()}
                                        >⧉</a>
                                        {(advRow && conductingRows.has(advRow.id)) || (!advRow && conductingRows.has(fallbackConductId)) ? (
                                          <span style={{ fontSize: '12px' }} title="Изменение проведения...">…</span>
                                        ) : (
                                          <span
                                            style={{ cursor: canToggleLinkedAdvance ? 'pointer' : 'default', opacity: canToggleLinkedAdvance ? 1 : 0.7 }}
                                            title={canToggleLinkedAdvance
                                              ? (advProcessed ? 'Снять проведение авансового СФ в СБИС' : 'Провести авансовый СФ в СБИС')
                                              : (advProcessed ? 'Проведено в СБИС' : 'Не проведено в СБИС')}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (canToggleLinkedAdvance) {
                                                handleToggleLinkedAdvanceConduct({ row, advRow, linkedInvNum, shouldConduct: !advProcessed });
                                              }
                                            }}
                                            data-testid={`btn-sbis-toggle-linked-advance-${docId}`}
                                          ><input type="checkbox" checked={!!advProcessed} readOnly style={{ width: '16px', height: '16px', accentColor: '#28a745', pointerEvents: 'none' }} /></span>
                                        )}
                                        {(advRow && deletingRows.has(advRow.id)) || (!advRow && deletingRows.has(fallbackDeleteId)) ? (
                                          <span style={{ fontSize: '13px' }} title="Удаление...">…</span>
                                        ) : (
                                          <span
                                            style={{ fontSize: '20px', cursor: canDeleteLinkedAdvance ? 'pointer' : 'default', opacity: canDeleteLinkedAdvance ? 0.6 : 1 }}
                                            title={canDeleteLinkedAdvance
                                              ? `Удалить авансовый СФ №${linkedInvNum} из СБИС`
                                              : 'Не удалось определить документ для удаления'}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (canDeleteLinkedAdvance) {
                                                handleDeleteLinkedAdvanceDocument({ row, advRow, linkedInvNum });
                                              }
                                            }}
                                            data-testid={`btn-sbis-delete-linked-advance-${docId}`}
                                          >✕</span>
                                        )}
                                      </div>
                                      {(advDate || linkedInvNum || row.linkedInvoiceDate) && (() => {
                                        const turboNum = (advRow?.number && advRow.number !== linkedInvNum) ? advRow.number : '';
                                        const sbisNum = linkedInvNum || '';
                                        const displayDate = advDate || row.linkedInvoiceDate || '';
                                        const hasArrow = turboNum && sbisNum;
                                        const label = hasArrow ? `${turboNum}=>${sbisNum}` : (sbisNum || turboNum);
                                        return (
                                          <span style={{ fontSize: '10px', color: '#888', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                            {label}{displayDate ? ` ${displayDate}` : ''}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  );
                                })()}
                              </div>
                            </td>
                              );
                            })()}
                            <td
                              style={{ ...cellStyle, minWidth: '20px', width: '20px', padding: '2px 0', fontWeight: 700, color: '#546E7A' }}
                              title={advanceVsShipmentSign
                                ? (hasAdvanceDate
                                  ? `Сравнение дат: ${advanceDateForCompare} ${advanceVsShipmentSign} ${shipmentDateForCompare}`
                                  : `Сравнение дат: ${paymentDateForCompare} ${advanceVsShipmentSign} ${shipmentDateForCompare}`)
                                : 'Нет данных для сравнения дат'}
                            >
                              {advanceVsShipmentSign}
                            </td>
                            {/* Отгрузка */}
                            <td
                              data-testid={`ship-cell-${_rowNum}-${_rowDate}`}
                              data-in-salesbook={_isInSalesBook ? 'true' : 'false'}
                              data-in-purchasebook={_isInPurchaseBook ? 'true' : 'false'}
                              style={{ ...cellStyle, ...((sbisCategory === 'shipment' && (isOutgoingShipmentInSalesBook || isIncomingReceiptInPurchaseBook)) || (sbisCategory === 'advance' && !!row.closingDoc && _isInSalesBook) ? { backgroundColor: '#d4edda' } : {}) }}
                                title={sbisCategory === 'shipment' ? 'Отгрузка / Реализация' : sbisCategory === 'advance' && row.closingDoc ? `Реализация №${row.closingDoc.number} от ${row.closingDoc.date}` : ''}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                {renderSbisCell(sbisCategory === 'shipment', { showTurbo: sbisCategory === 'shipment' })}
                                {sbisCategory === 'shipment' && (row.date || row.number || row.sbisNumber) && (
                                  <span style={{ fontSize: '10px', color: '#888', lineHeight: 1, whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>
                                    {(() => {
                                      const turboNum = row.number || '';
                                      const sbisNum = row.sbisNumber || '';
                                      const hasArrow = turboNum && sbisNum && turboNum !== sbisNum;
                                      const label = hasArrow ? `${turboNum}=>${sbisNum}` : (sbisNum || turboNum);
                                      return `${label}${row.date ? ` ${row.date}` : ''}`;
                                    })()}{row.hasLinkedInvoice && (showLinkedAdvance
                                      ? null // shown in Аванс.СФ column instead
                                      : row.linkedInvoiceLink
                                        ? <a href={row.linkedInvoiceLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none' }}><sup style={{ fontSize: '9px', color: '#1565C0', fontWeight: 'bold', marginLeft: '1px' }} title="Открыть счёт-фактуру в СБИС">СФ</sup></a>
                                        : <sup style={{ fontSize: '9px', color: '#1565C0', fontWeight: 'bold', marginLeft: '1px' }} title="Привязана счёт-фактура">СФ</sup>
                                    )}
                                  </span>
                                )}
                                {sbisCategory === 'advance' && row.closingDoc && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                    {(() => {
                                      const closingNum = row.closingDoc?.number || '';
                                      const closingProcessed = !!row.closingDoc?.processed;
                                      const fallbackConductId = `closing_${row.id || closingNum || ''}`;
                                      const fallbackDeleteId = `closing_del_${row.id || closingNum || ''}`;
                                      const canToggleClosing = !!extractGuidFromSbisLink(row.closingDoc?.sbisLink);
                                      const canDeleteClosing = !!extractGuidFromSbisLink(row.closingDoc?.sbisLink);
                                      return (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '3px' }}>
                                          {row.closingDoc.sbisLink ? (
                                            <a href={row.closingDoc.sbisLink} target="_blank" rel="noopener noreferrer"
                                              style={{ textDecoration: 'none', fontSize: '21px' }}
                                              title={`Открыть реализацию №${row.closingDoc.number} в СБИС`}
                                              onClick={(e) => e.stopPropagation()}
                                            >⧉</a>
                                          ) : <span style={{ fontSize: '14px' }}>◻</span>}
                                          {conductingRows.has(fallbackConductId) ? (
                                            <span style={{ fontSize: '12px' }} title="Изменение проведения...">…</span>
                                          ) : (
                                            <span
                                              style={{ fontSize: '18px', cursor: canToggleClosing ? 'pointer' : 'default', opacity: canToggleClosing ? (closingProcessed ? 1 : 0.7) : 1 }}
                                              title={canToggleClosing
                                                ? (closingProcessed ? 'Снять проведение реализации в СБИС' : 'Провести реализацию в СБИС')
                                                : (closingProcessed ? 'Проведено в СБИС' : 'Не проведено в СБИС')}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (canToggleClosing) {
                                                  handleToggleClosingShipmentConduct({ row, shouldConduct: !closingProcessed });
                                                }
                                              }}
                                              data-testid={`btn-sbis-toggle-closing-shipment-${docId}`}
                                            ><input type="checkbox" checked={!!closingProcessed} readOnly style={{ width: '16px', height: '16px', accentColor: '#28a745', cursor: canToggleClosing ? 'pointer' : 'default', pointerEvents: 'none' }} /></span>
                                          )}
                                          {deletingRows.has(fallbackDeleteId) ? (
                                            <span style={{ fontSize: '13px' }} title="Удаление...">…</span>
                                          ) : (
                                            <span
                                              style={{ fontSize: '20px', cursor: canDeleteClosing ? 'pointer' : 'default', opacity: canDeleteClosing ? 0.6 : 1 }}
                                              title={canDeleteClosing
                                                ? `Удалить реализацию №${closingNum} из СБИС`
                                                : 'Не удалось определить документ для удаления'}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (canDeleteClosing) {
                                                  handleDeleteClosingShipmentDocument({ row });
                                                }
                                              }}
                                              data-testid={`btn-sbis-delete-closing-shipment-${docId}`}
                                            >✕</span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    {(row.closingDoc.number || row.closingDoc.date) && (
                                      <span style={{ fontSize: '10px', color: '#888', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                        {row.closingDoc.number || ''}{row.closingDoc.date ? ` ${row.closingDoc.date}` : ''}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </>
                        );
                      })()}
                      <td>
                        <span
                          style={{
                            cursor: 'pointer',
                            textDecoration: contractorFilter === row.contractor ? 'none' : 'underline',
                            fontWeight: contractorFilter === row.contractor ? 'bold' : 'normal',
                            color: contractorFilter === row.contractor ? '#1a73e8' : 'inherit'
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            setContractorFilter(prev => prev === row.contractor ? '' : row.contractor);
                          }}
                          title={contractorFilter === row.contractor ? 'Сбросить фильтр по контрагенту' : 'Фильтровать по этому контрагенту'}
                        >
                          {row.contractor}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span
                          style={{
                            cursor: 'pointer',
                            fontWeight: amountFilter === row.amount ? 'bold' : 'normal',
                            color: amountFilter === row.amount ? '#1a73e8' : 'inherit'
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            setAmountFilter(prev => prev === row.amount ? null : row.amount);
                          }}
                          title={amountFilter === row.amount ? 'Сбросить фильтр по сумме' : 'Фильтровать по этой сумме'}
                        >
                          {row.amount.toLocaleString("ru-RU")}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontSize: '11px', whiteSpace: 'nowrap' }}>
                        {(() => {
                          // vatRateDisplay от бэкенда: "payRate/advRate/docRate"
                          const vatParts = (row.vatRateDisplay || '-/-/-').split('/');
                          const backendPayRate = vatParts[0] || '-';
                          const backendDocRate = vatParts[2] || '-';

                          const vatAmt = Math.max(0, row.vat20 || row.vat22 || row.vat || 0);

                          const isPaymentDoc2 = row.isPayment || row.docType === 'ВходящийПлатеж' || row.docType === 'ИсходящийПлатеж' || row.docType === 'БанковскаяВыписка';
                          const isAdvance2 = /\/АВ/i.test(row.sbisNumber || '') || /\/АВ/i.test(row.number || '') || /ПР$/i.test(row.number || '') || /ПР$/i.test(row.sbisNumber || '');
                          const cat = isAdvance2 ? 'advance' : isPaymentDoc2 ? 'payment' : 'shipment';

                          // Ставка НДС по году: 2026+ -> 22%, до 2026 -> 20%
                          const extractYear = (dateValue) => {
                            const s = String(dateValue || '').trim();
                            const isoMatch = s.match(/^(\d{4})[-/]/);
                            if (isoMatch) return parseInt(isoMatch[1], 10);
                            const ruMatch = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
                            if (ruMatch) return parseInt(ruMatch[3], 10);
                            const anyYearMatch = s.match(/(\d{4})/);
                            return anyYearMatch ? parseInt(anyYearMatch[1], 10) : NaN;
                          };
                          const rateByYear = (dateValue) => {
                            const y = extractYear(dateValue);
                            return Number.isFinite(y) && y >= 2026 ? 22 : 20;
                          };
                          const paymentDate = (Array.isArray(row.payments) && row.payments.length > 0)
                            ? String(row.payments[0].date || row.date || '')
                            : String(row.date || '');
                          const yearBasedRate = rateByYear(paymentDate);

                          const hasPayments = cat === 'payment' || (Array.isArray(row.payments) && row.payments.length > 0);
                          const paymentsTotal = Array.isArray(row.payments) ? row.payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0) : 0;

                          // === СЛОТ 1: Оплата ===
                          let paySlotRate = '-';
                          let payAmt = 0;
                          if (hasPayments) {
                            if (backendPayRate === '0') {
                              // Оплата явно без НДС по данным бэкенда
                              paySlotRate = '0';
                            } else {
                              const payBackendRateNum = parseInt(backendPayRate, 10);
                              const effectivePayRate = payBackendRateNum > 0 ? payBackendRateNum : yearBasedRate;
                              paySlotRate = String(effectivePayRate);
                              if (cat === 'payment') {
                                payAmt = vatAmt > 0 ? vatAmt : Math.round(row.amount * effectivePayRate / (100 + effectivePayRate));
                              } else {
                                payAmt = paymentsTotal > 0 ? Math.round(paymentsTotal * effectivePayRate / (100 + effectivePayRate)) : 0;
                              }
                            }
                          }

                          // === СЛОТ 2: Авансовая СФ ===
                          let advSlotRate = '-';
                          let advAmt = 0;
                          if (cat === 'advance') {
                            // Ставка: из VAT полей документа, или год-based (как платежи)
                            const advDocRate = parseInt(backendDocRate, 10);
                            const advEffectiveRate = (advDocRate > 0) ? advDocRate : yearBasedRate;
                            advSlotRate = String(advEffectiveRate);
                            // Сумма: из VAT полей или вычисляем из суммы документа
                            advAmt = vatAmt > 0 ? vatAmt : (row.amount > 0 ? Math.round(row.amount * advEffectiveRate / (100 + advEffectiveRate)) : 0);
                          }

                          // === СЛОТ 3: Отгрузка/Реализация ===
                          let docSlotRate = '-';
                          let docAmt = 0;
                          if (cat === 'shipment') {
                            docSlotRate = backendDocRate;
                            docAmt = vatAmt;
                          } else if (cat === 'advance' && row.closingDoc) {
                            // У авансовой СФ есть привязанная реализация — берём её VAT
                            const cd = row.closingDoc;
                            const cdVatAmt = cd.vat20 || cd.vat22 || cd.vat || 0;
                            const cdAmount = cd.amount || 0;
                            const cdDate = cd.date || '';
                            const cdYearRate = rateByYear(cdDate);
                            if (cdVatAmt > 0) {
                              docAmt = cdVatAmt;
                              // Определяем ставку из полей
                              docSlotRate = cd.vat22 > 0 ? '22' : cd.vat20 > 0 ? '20' : String(cdYearRate);
                            } else if (cdAmount > 0) {
                              docSlotRate = String(cdYearRate);
                              docAmt = Math.round(cdAmount * cdYearRate / (100 + cdYearRate));
                            }
                          }

                          const fmt = (amt, rate) => {
                            if (rate === '-') return '-';
                            if (amt > 0) return <><strong>{amt.toLocaleString('ru-RU')}</strong><span style={{color:'#888'}}>({rate})</span></>;
                            return <span style={{color:'#888'}}>{rate}</span>;
                          };
                          const p = fmt(payAmt, paySlotRate);
                          const a = fmt(advAmt, advSlotRate);
                          const d = fmt(docAmt, docSlotRate);
                          if (paySlotRate === '-' && advSlotRate === '-' && docSlotRate === '-') return '';
                          return <>{p}<span style={{color:'#bbb'}}>/</span>{a}<span style={{color:'#bbb'}}>/</span>{d}</>;
                        })()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Модальное окно с данными CRD */}
        {isCrdModalOpen && selectedCrdData && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={() => setIsCrdModalOpen(false)}
            data-testid="crd-modal-overlay"
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '90%',
                width: '1000px',
                maxHeight: '90vh',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="crd-modal"
            >
              {/* Заголовок */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '20px 20px 15px 20px',
                borderBottom: '1px solid #e0e0e0'
              }}>
                <h2 style={{ margin: 0 }}>
                  Данные из CRD файла
                  {selectedCrdData.tableName && (
                    <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                      ({selectedCrdData.tableName}{selectedCrdData.yearMonth ? ` - ${selectedCrdData.yearMonth}` : ''})
                    </span>
                  )}
                </h2>
                <button 
                  onClick={() => setIsCrdModalOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                  data-testid="btn-crd-modal-close"
                >
                  ✕
                </button>
              </div>

              {/* Вкладки — показываем только когда данные загружены */}
              {selectedCrdData.crdData && !selectedCrdData.loading && !selectedCrdData.error && (
                <div style={{
                  display: 'flex',
                  borderBottom: '2px solid #e0e0e0',
                  padding: '0 20px',
                  backgroundColor: '#fafafa'
                }}>
                  {[['normal', '📄 Обычное'], ['technical', '🔧 Техническое']].map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setCrdModalTab(key)}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderBottom: crdModalTab === key ? '2px solid #007bff' : '2px solid transparent',
                        marginBottom: '-2px',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: crdModalTab === key ? '#007bff' : '#555',
                        fontWeight: crdModalTab === key ? '600' : 'normal'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Прокручиваемый контент */}
              <div style={{
                flex: '1',
                overflow: 'auto',
                padding: '20px'
              }}>

              {selectedCrdData.loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Загрузка данных из CRD...</p>
                </div>
              ) : selectedCrdData.error ? (
                <div style={{ 
                  backgroundColor: '#f8d7da', 
                  color: '#721c24', 
                  padding: '20px', 
                  borderRadius: '4px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: '0 0 10px 0' }}>❌ Ошибка загрузки данных</h3>
                  <p style={{ margin: 0 }}>{selectedCrdData.error}</p>
                </div>
              ) : selectedCrdData.crdData ? (
                crdModalTab === 'normal' ? (() => {
                  const d = selectedCrdData.crdData;
                  const tbl = (selectedCrdData.tableName || '').toLowerCase();
                  const isIncoming = tbl.includes('пост');
                  const number = d.НОМЕРБУМ || d.НОМЕРД || d.НОМЕР || d.Номер || '—';
                  const date = d.ДАТА || '—';
                  const counterpartyName = isIncoming
                    ? (d.НАИМПОСТ || d.ПОКУП || '—')
                    : (d.ПОКУП || d.НАИМПОСТ || '—');
                  const inn = isIncoming
                    ? (d.ИННПОСТ || d.ИНН || '')
                    : (d.ИНН || d.ИННПОК || '');
                  const kpp = isIncoming
                    ? (d.КПППОСТ || d.КПП || '')
                    : (d.КПП || d.КПППОК || '');
                  const totalAmount = parseFloat(
                    String(d.ВСЕГОДОК || d.ВСЕГОПОК || d.ВСЕГО || d.СУММА || 0).replace(',', '.')
                  ) || 0;
                  const comment = d.КОММЕНТ || d.КОММЕНТАРИЙ || '';
                  const items = Array.isArray(d.КОЛНАИМ) ? d.КОЛНАИМ : [];
                  const fmtMoney = (v) => {
                    const n = parseFloat(String(v || 0).replace(',', '.')) || 0;
                    return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  };
                  const vatRows = [
                    { rate: '20%', base: d.СУМ1,  tax: d.СТО1  },
                    { rate: '22%', base: d.СУМ22, tax: d.СТО22 },
                    { rate: '18%', base: d.СУМ18, tax: d.СТО18 },
                    { rate: '10%', base: d.СУМ2,  tax: d.СТО2  },
                  ].filter(r =>
                    parseFloat(String(r.base || 0).replace(',', '.')) > 0 ||
                    parseFloat(String(r.tax  || 0).replace(',', '.')) > 0
                  );
                  const sectionStyle = {
                    border: '1px solid #e8e8e8',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    backgroundColor: '#fff'
                  };
                  const labelStyle = { color: '#888', fontSize: '11px', marginBottom: '2px' };
                  const valueStyle = { fontSize: '13px', fontWeight: '500' };
                  const inlineField = (label, value) => value ? (
                    <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                      <span style={{ color: '#888' }}>{label}: </span>
                      <span style={{ fontWeight: '500' }}>{value}</span>
                    </div>
                  ) : null;
                  const thStyle = {
                    padding: '6px 10px',
                    textAlign: 'left',
                    backgroundColor: '#f0f4f8',
                    fontSize: '12px',
                    color: '#555',
                    fontWeight: '600',
                    borderBottom: '1px solid #ddd'
                  };
                  const tdStyle = {
                    padding: '5px 10px',
                    fontSize: '12px',
                    borderBottom: '1px solid #f0f0f0',
                    verticalAlign: 'top'
                  };
                  return (
                    <div style={{ fontSize: '13px' }}>
                      {/* Реквизиты документа */}
                      <div style={sectionStyle}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                          <div>
                            <div style={labelStyle}>Номер</div>
                            <div style={valueStyle}>{number}</div>
                          </div>
                          <div>
                            <div style={labelStyle}>Дата</div>
                            <div style={valueStyle}>{date}</div>
                          </div>
                          <div>
                            <div style={labelStyle}>UKEY</div>
                            <div style={{ ...valueStyle, fontFamily: 'monospace', fontSize: '12px' }}>{d.UKEY || '—'}</div>
                          </div>
                          {comment && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <div style={labelStyle}>Комментарий</div>
                              <div style={valueStyle}>{comment}</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Стороны */}
                      <div style={{ display: 'grid', gridTemplateColumns: isIncoming ? '1fr 1fr' : '1fr', gap: '10px', marginBottom: '12px' }}>
                        {isIncoming && (
                          <div style={sectionStyle}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#007bff', marginBottom: '8px', textTransform: 'uppercase' }}>
                              Поставщик
                            </div>
                            {inlineField('Наименование', counterpartyName)}
                            {inlineField('ИНН', inn)}
                            {inlineField('КПП', kpp)}
                          </div>
                        )}
                        <div style={{ ...sectionStyle, textAlign: 'left' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#28a745', marginBottom: '8px', textTransform: 'uppercase' }}>
                            Покупатель
                          </div>
                          {!isIncoming ? (
                            <>
                              {inlineField('Наименование', counterpartyName)}
                              {inlineField('ИНН', inn)}
                              {inlineField('КПП', kpp)}
                              {inlineField('Адрес', d.АДРЕС)}
                            </>
                          ) : (
                            <div style={{ fontSize: '12px', fontWeight: '500' }}>Наша организация</div>
                          )}
                        </div>
                      </div>

                      {/* Таблица товаров */}
                      {items.length > 0 && (
                        <div style={{ ...sectionStyle, padding: '0', overflow: 'hidden', marginBottom: '12px' }}>
                          <div style={{ padding: '8px 12px', backgroundColor: '#f0f4f8', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: '600', color: '#444' }}>
                            Товары / Услуги ({items.length} поз.)
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                              <thead>
                                <tr>
                                  <th style={{ ...thStyle, width: '30px' }}>№</th>
                                  <th style={thStyle}>Наименование</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Кол-во</th>
                                  <th style={{ ...thStyle, textAlign: 'center' }}>Ед.</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Цена</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Сумма без НДС</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>НДС</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Итого</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((item, idx) => (
                                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ ...tdStyle, color: '#999' }}>{idx + 1}</td>
                                    <td style={tdStyle}>{item.НАИМ || '—'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{item.КОЛИЧ || '—'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>{item.ЕДИЗМ || '—'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(item.ЦЕНА)}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(item.СУММА)}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: '#666' }}>
                                      {fmtMoney(item.СУММАНДС)}
                                      {item.НДС ? <span style={{ fontSize: '10px', color: '#999', marginLeft: '3px' }}>({item.НДС}%)</span> : ''}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '500' }}>{fmtMoney(item.ВСЕГО)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Итоги */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ ...sectionStyle, minWidth: '300px' }}>
                          {vatRows.map((r, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', marginBottom: '4px', fontSize: '12px' }}>
                              <span style={{ color: '#666' }}>НДС {r.rate} (с {fmtMoney(r.base)} руб.):</span>
                              <span>{fmtMoney(r.tax)} руб.</span>
                            </div>
                          ))}
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', paddingTop: '6px', borderTop: '1px solid #e0e0e0', marginTop: '4px' }}>
                            <span style={{ fontWeight: '700', fontSize: '13px' }}>Итого с НДС:</span>
                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#007bff' }}>
                              {fmtMoney(totalAmount)} руб.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
                : (
                <div>
                  {/* JSON отображение данных с подсветкой синтаксиса */}
                  <pre style={{
                    backgroundColor: '#1e1e1e',
                    padding: '20px',
                    borderRadius: '4px',
                    border: '1px solid #333',
                    overflow: 'auto',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    margin: 0,
                    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                    whiteSpace: 'pre',
                    textAlign: 'left'
                  }}>
                    <code style={{ display: 'block' }} dangerouslySetInnerHTML={{
                      __html: (() => {
                        const json = JSON.stringify(selectedCrdData.crdData, null, 2);
                        return json
                          .replace(/&/g, '&amp;')
                          .replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;')
                          .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
                            let cls = 'number';
                            if (/^"/.test(match)) {
                              if (/:$/.test(match)) {
                                cls = 'key';
                              } else {
                                cls = 'string';
                              }
                            } else if (/true|false/.test(match)) {
                              cls = 'boolean';
                            } else if (/null/.test(match)) {
                              cls = 'null';
                            }
                            const colors = {
                              key: '#9cdcfe',
                              string: '#ce9178',
                              number: '#b5cea8',
                              boolean: '#569cd6',
                              null: '#569cd6'
                            };
                            return `<span style="color: ${colors[cls]}">${match}</span>`;
                          })
                          .replace(/([{}\[\],])/g, '<span style="color: #d4d4d4">$1</span>');
                      })()
                    }} />
                  </pre>
                </div>
                )
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>Нет данных для отображения</p>
                </div>
              )}
              </div>

              {/* Закрепленный футер с кнопками */}
              <div style={{ 
                borderTop: '1px solid #e0e0e0',
                padding: '15px 20px',
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '10px',
                backgroundColor: '#f8f9fa'
              }}>
                <button
                  onClick={() => setIsCrdModalOpen(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Закрыть
                </button>
                {selectedCrdData.crdData && !selectedCrdData.loading && !selectedCrdData.error && (() => {
                  // Определяем, есть ли документ в СБИС
                  const hasSbis = selectedCrdData.source === 'sbis' || 
                                 selectedCrdData.hasSbis || 
                                 (selectedCrdData.sources && selectedCrdData.sources.includes('sbis'));
                  const sbisLink = selectedCrdData.sbisLink || '';
                  
                  if (hasSbis && sbisLink) {
                    // Если документ есть в СБИС и есть ссылка - показываем кнопку "Открыть в СБИС"
                    return (
                      <button
                        onClick={() => {
                          window.open(sbisLink, '_blank', 'noopener,noreferrer');
                        }}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        title="Открыть документ в СБИС"
                      >
                        ⧉ Открыть в СБИС
                      </button>
                    );
                  } else if (selectedCrdData.creating) {
                    return (
                      <button
                        disabled
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'not-allowed',
                          fontSize: '14px',
                          opacity: 0.7
                        }}
                      >
                        … Создание...
                      </button>
                    );
                  } else {
                    // Если документа нет в СБИС - показываем кнопку "Создать в СБИС"
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        {selectedCrdData.createError && (
                          <div style={{ fontSize: '12px', color: '#dc3545' }}>
                            ✗ {selectedCrdData.createError}
                          </div>
                        )}
                        <button
                          onClick={() => handleCreateSbisDocument(selectedCrdData)}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          title="Создать документ в СБИС с XML вложением"
                          data-testid="btn-crd-modal-create-sbis"
                        >
                          + Создать в СБИС
                        </button>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default Dashboard;
